const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

class ApiClient {
  private _accessToken: string | null = null;
  private _isRefreshing = false;

  setAccessToken(token: string | null) {
    this._accessToken = token;
  }

  getAccessToken(): string | null {
    return this._accessToken;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...options.headers,
    };

    if (this._accessToken) {
      (headers as Record<string, string>)["Authorization"] = `Bearer ${this._accessToken}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: "include",
    });

    // Auto-refresh on 401 (skip for refresh/login/register endpoints)
    if (
      response.status === 401 &&
      this._accessToken &&
      !this._isRefreshing &&
      !endpoint.includes("/auth/refresh") &&
      !endpoint.includes("/auth/login") &&
      !endpoint.includes("/auth/register")
    ) {
      const refreshed = await this.attemptTokenRefresh();
      if (refreshed) {
        // Retry original request with new access token
        const retryHeaders: HeadersInit = {
          "Content-Type": "application/json",
          ...options.headers,
        };
        (retryHeaders as Record<string, string>)["Authorization"] = `Bearer ${this._accessToken}`;

        const retryResponse = await fetch(`${API_URL}${endpoint}`, {
          ...options,
          headers: retryHeaders,
          credentials: "include",
        });
        return retryResponse.json();
      }
    }

    const data = await response.json();
    return data;
  }

  private async attemptTokenRefresh(): Promise<boolean> {
    const refreshToken = localStorage.getItem("kvanten_refresh_token");
    if (!refreshToken) return false;

    this._isRefreshing = true;
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      const data = await response.json();

      if (data.success && data.data?.tokens) {
        this._accessToken = data.data.tokens.accessToken;
        localStorage.setItem("kvanten_access_token", data.data.tokens.accessToken);
        localStorage.setItem("kvanten_refresh_token", data.data.tokens.refreshToken);
        return true;
      }
    } catch {
      // Refresh failed silently
    } finally {
      this._isRefreshing = false;
    }

    // Refresh failed — clear tokens
    this._accessToken = null;
    localStorage.removeItem("kvanten_access_token");
    localStorage.removeItem("kvanten_refresh_token");
    return false;
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "GET" });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "DELETE",
      body: body ? JSON.stringify(body) : undefined,
    });
  }
}

export const api = new ApiClient();

// Auth-specific API functions
export interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  emailVerified?: boolean;
  createdAt?: string;
  hasAdminAccess?: boolean;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface AuthResult {
  user: User;
  tokens: AuthTokens;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  acceptTerms: boolean;
  acceptPrivacy: boolean;
}

export interface LoginData {
  email: string;
  password: string;
  trustedDeviceToken?: string;
}

// 2FA Types
export interface TwoFactorChallengeResponse {
  requires2FA: true;
  challengeToken: string;
  user: {
    email: string;
    firstName: string | null;
  };
}

export interface TwoFactorVerifyData {
  challengeToken: string;
  code: string;
  trustDevice?: boolean;
}

export interface TwoFactorVerifyResult {
  user: User;
  tokens: AuthTokens;
  trustedDeviceToken?: string;
  backupCodesRemaining?: number;
}

export interface TwoFactorStatus {
  enabled: boolean;
  verified: boolean;
  enabledAt: string | null;
  backupCodesRemaining: number;
}

export interface TwoFactorSetupResult {
  qrCodeUrl: string;
  secret: string;
  backupCodes: string[];
}

export interface TrustedDevice {
  id: string;
  deviceName: string | null;
  browser: string | null;
  os: string | null;
  ipAddress: string | null;
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string;
}

export interface SessionInfo {
  id: string;
  deviceName: string;
  browser: string;
  os: string;
  ipAddress: string | null;
  createdAt: string;
  expiresAt: string;
  isCurrent: boolean;
}

// Login response can be either a normal auth result or a 2FA challenge
export type LoginResponse = AuthResult | TwoFactorChallengeResponse;

export function isTwoFactorChallenge(response: LoginResponse): response is TwoFactorChallengeResponse {
  return 'requires2FA' in response && response.requires2FA === true;
}

export const authApi = {
  register: (data: RegisterData) => api.post<AuthResult>("/auth/register", data),
  login: (data: LoginData) => api.post<LoginResponse>("/auth/login", data),
  logout: (refreshToken?: string) =>
    api.post<{ message: string }>("/auth/logout", { refreshToken }),
  refresh: (refreshToken: string) =>
    api.post<AuthResult>("/auth/refresh", { refreshToken }),
  me: () => api.get<{ user: User }>("/auth/me"),
  forgotPassword: (email: string) => api.post<{ message: string }>("/auth/forgot-password", { email }),
  resetPassword: (token: string, newPassword: string) => api.post<{ message: string }>("/auth/reset-password", { token, password: newPassword }),
  verifyEmail: (token: string) => api.post<{ message: string }>("/auth/verify-email", { token }),
  resendVerification: (email: string) => api.post<{ message: string }>("/auth/resend-verification", { email }),
  updateProfile: (data: { firstName?: string; lastName?: string }) =>
    api.patch<{ firstName: string | null; lastName: string | null }>("/auth/me", data),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ message: string }>("/auth/change-password", { currentPassword, newPassword }),
  deleteAccount: (password: string) =>
    api.delete<{ message: string }>("/auth/me", { password }),

  // Session management
  getSessions: (refreshToken?: string) =>
    api.post<{ sessions: SessionInfo[] }>("/auth/sessions", { refreshToken }),
  revokeSession: (sessionId: string) =>
    api.delete<{ message: string }>(`/auth/sessions/${sessionId}`),
  revokeAllOtherSessions: (refreshToken: string) =>
    api.post<{ message: string; revokedCount: number }>("/auth/sessions/revoke-others", { refreshToken }),
};

// 2FA API
export const twoFactorApi = {
  // Get 2FA status
  getStatus: () => api.get<TwoFactorStatus>("/2fa/status"),

  // Start 2FA setup
  setup: () => api.post<TwoFactorSetupResult>("/2fa/setup"),

  // Verify setup and enable 2FA
  verifySetup: (code: string) =>
    api.post<{ message: string }>("/2fa/verify-setup", { code }),

  // Disable 2FA
  disable: (password: string) =>
    api.post<{ message: string }>("/2fa/disable", { password }),

  // Regenerate backup codes
  regenerateBackupCodes: (password: string) =>
    api.post<{ backupCodes: string[] }>("/2fa/regenerate-backup-codes", { password }),

  // Get trusted devices
  getTrustedDevices: () =>
    api.get<{ devices: TrustedDevice[] }>("/2fa/trusted-devices"),

  // Remove a trusted device
  removeTrustedDevice: (deviceId: string) =>
    api.delete<{ message: string }>(`/2fa/trusted-devices/${deviceId}`),

  // Remove all trusted devices
  removeAllTrustedDevices: () =>
    api.delete<{ message: string }>("/2fa/trusted-devices"),

  // Verify 2FA code during login
  verify: (data: TwoFactorVerifyData) =>
    api.post<TwoFactorVerifyResult>("/auth/2fa/verify", data),

  // Verify backup code during login
  verifyBackup: (data: TwoFactorVerifyData) =>
    api.post<TwoFactorVerifyResult>("/auth/2fa/verify-backup", data),
};

// Company types
export type FinancialHealthProfile =
  | "holding"
  | "investment"
  | "property"
  | "knowledge"
  | "service"
  | "trading"
  | "manufacturing"
  | "general";

export interface Company {
  id: string;
  organizationNumber: string;
  name: string;
  type: "AB" | "EF" | "HB" | "KB";
  seatMunicipality: string | null;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  financialHealthProfile: FinancialHealthProfile | null;
  createdAt: string;
  role?: string;
}

export interface CompanyWithDetails extends Company {
  boardMembers: BoardMember[];
  annualReports: AnnualReportSummary[];
}

export interface BoardMember {
  id: string;
  name: string;
  role: string;
  personalId: string; // Obligatoriskt för personmatchning
  email?: string | null; // E-postadress för kallelser
}

export interface CreateCompanyData {
  organizationNumber: string;
  name: string;
  type?: "AB" | "EF" | "HB" | "KB";
  seatMunicipality?: string;
  address: string;
  postalCode: string;
  city: string;
}

export interface UpdateCompanyData {
  name?: string;
  type?: "AB" | "EF" | "HB" | "KB";
  seatMunicipality?: string;
  address?: string;
  postalCode?: string;
  city?: string;
}

// Bolagsverket lookup types
export interface CompanyLookupData {
  organizationNumber: string;
  name: string;
  type: "AB" | "EF" | "HB" | "KB";
  seatMunicipality?: string;
  address?: {
    street?: string;
    postalCode?: string;
    city?: string;
  };
  shareCapital?: number;
  businessDescription?: string;
}

// Financial highlights for report summary cards
export interface FinancialHighlights {
  netResult?: number;      // Årets resultat
  netSales?: number;       // Nettoomsättning
  totalAssets?: number;    // Balansomslutning
}

// Annual Report types
export interface AnnualReportSummary {
  id: string;
  fiscalYearStart: string;
  fiscalYearEnd: string;
  status: "draft" | "in_progress" | "completed" | "finalized" | "exported";
  currentStep: number;
  accountingStandard: AccountingStandard;
  createdAt: string;
  completedAt: string | null;
  exportedAt: string | null;
  updatedAt: string;
  financialHighlights?: FinancialHighlights | null;
}

export interface AnnualReport extends AnnualReportSummary {
  companyId: string;
  sieFileId: string | null;
  parsedSieData: ParsedSieData | null;
  userInput: UserInput;
  calculations: Calculations | null;
  validationResult: ValidationResult | null;
  company?: Company;
  previousYearUserInput?: UserInput | null;
}

// Balance sheet type for reuse
export interface BalanceSheet {
  assets: {
    fixedAssets: {
      intangible: number;
      buildings?: number;   // 11xx - Byggnader och mark
      machinery?: number;   // 12xx - Inventarier, verktyg och installationer
      tangible: number;     // 11xx + 12xx combined
      financial: number;
      total: number;
    };
    currentAssets: {
      inventory: number;
      tradeReceivables?: number;   // 15xx - Kundfordringar
      otherReceivables?: number;   // 16xx - Övriga fordringar
      prepaidExpenses?: number;    // 17xx - Förutbetalda kostnader
      receivables: number;         // 15xx-17xx combined
      shortTermInvestments: number;
      cashAndBank: number;
      total: number;
    };
    totalAssets: number;
  };
  equityAndLiabilities: {
    equity: {
      // Bundet eget kapital
      shareCapital: number;              // 2081 - Aktiekapital
      unregisteredShareCapital: number;  // 2082 - Ej registrerat aktiekapital
      revaluationReserve: number;        // 2084 - Uppskrivningsfond (bundet EK)
      legalReserve: number;              // 2086 - Reservfond (bundet EK)
      developmentFund: number;           // 2089 - Fond för utvecklingsutgifter (bundet)
      // Fritt eget kapital
      sharePremium: number;              // 2085 + 2097 - Överkursfond (fritt EK)
      retainedEarnings: number;          // Summa fritt EK exkl årets resultat
      currentYearResult: number;         // 2099 - Årets resultat
      // Detaljerade komponenter för förändring av eget kapital
      details: {
        retainedEarningsOnly: number;    // 2091 - Balanserat resultat
        groupContributions: number;      // 2092 - Mottagna/lämnade koncernbidrag
        shareholderContributions: number; // 2093 - Erhållna aktieägartillskott
        ownShares: number;               // 2094 - Egna aktier (minskar EK!)
        mergerResult: number;            // 2095 - Fusionsresultat
        prevYearProfitLoss: number;      // 2098 - Vinst/förlust föregående år
        sharePremium2085: number;        // 2085 - Överkursfond (ny)
        sharePremiumOld2097: number;     // 2097 - Överkursfond (före 2006)
      };
      total: number;
    };
    untaxedReserves: number;
    provisions: number;
    longTermLiabilities: number;
    tradePayables?: number;           // 24xx - Leverantörsskulder
    taxLiabilities?: number;          // 25xx - Skatteskulder
    otherCurrentLiabilities?: number; // 26xx-28xx - Övriga kortfristiga skulder
    accruedExpenses?: number;         // 29xx - Upplupna kostnader
    shortTermLiabilities: number;     // 24xx-29xx combined
    totalEquityAndLiabilities: number;
  };
}

// Income statement type for reuse
export interface IncomeStatement {
  revenue: { netSales: number; otherIncome: number; total: number };
  expenses: { goodsAndMaterials: number; otherExternalCosts: number; personnelCosts: number; depreciationAmortization: number; otherOperatingExpenses: number; total: number };
  operatingResult: number;
  financialItems: { financialIncome: number; financialExpenses: number; total: number };
  resultAfterFinancialItems: number;
  extraordinaryItems: number;
  appropriations: number;
  taxOnResult: number;
  netResult: number;
}

// Raw SIE account-level data (stored in JSONB, available on frontend)
export interface SieBalance {
  accountNumber: string;
  yearIndex: number; // 0 = current year, -1 = previous year
  amount: number;
}

export interface SieResult {
  accountNumber: string;
  yearIndex: number;
  amount: number;
}

export interface SieAccount {
  number: string;
  name: string;
  type?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// EQUITY CHANGE ANALYSIS - K2 5.7 - Förändring av eget kapital
// ═══════════════════════════════════════════════════════════════════════════

export type EquityChangeType =
  | 'share_issue'                  // Nyemission
  | 'bonus_issue'                  // Fondemission
  | 'capital_reduction'            // Kapitalnedsättning
  | 'shareholder_contribution'     // Aktieägartillskott (mottaget)
  | 'contribution_repayment'       // Återbetalning av villkorat tillskott
  | 'dividend_paid'                // Utbetald utdelning (fg års beslut)
  | 'group_contribution_received'  // Mottaget koncernbidrag
  | 'group_contribution_given'     // Lämnat koncernbidrag
  | 'reserve_transfer_to'          // Avsättning till reservfond
  | 'reserve_transfer_from'        // Ianspråktagande av reservfond
  | 'loss_coverage'                // Täckning av förlust
  | 'previous_year_result'         // Överföring av föregående års resultat
  | 'other';                       // Övrig förändring

export interface EquityChange {
  type: EquityChangeType;
  amount: number;
  description?: string;
  detectedFromSie: boolean;
  requiresUserInput: boolean;
  suggestedDescription?: string;
}

export interface EquityComponentAnalysis {
  opening: number;
  closing: number;
  netChange: number;
  changes: EquityChange[];
  hasUnexplainedChange: boolean;
}

export interface EquityChangeAnalysis {
  shareCapital: EquityComponentAnalysis;
  unregisteredShareCapital: EquityComponentAnalysis;
  revaluationReserve: EquityComponentAnalysis;
  legalReserve: EquityComponentAnalysis;
  developmentFund: EquityComponentAnalysis;
  sharePremium: EquityComponentAnalysis;
  retainedEarnings: EquityComponentAnalysis;
  shareholderContributions: EquityComponentAnalysis;
  groupContributions: EquityComponentAnalysis;
  ownShares: EquityComponentAnalysis;
  currentYearResult: number;
  summary: {
    totalEquityOpening: number;
    totalEquityClosing: number;
    totalNetChange: number;
    hasAnyChanges: boolean;
    hasChangesRequiringInput: boolean;
    changesRequiringInput: string[];
  };
}

export interface ParsedSieData {
  metadata: {
    format: string;
    program: string;
    programVersion: string;
    generatedDate: string;
    companyName: string;
    organizationNumber: string;
    fiscalYearStart: string;
    fiscalYearEnd: string;
    currency: string;
    encoding: string;
  };
  // Raw account-level data from SIE parser
  accounts?: SieAccount[];
  openingBalances?: SieBalance[];
  closingBalances?: SieBalance[];
  results?: SieResult[];
  // Aggregated structured data
  balanceSheet: BalanceSheet;
  incomeStatement: IncomeStatement;
  keyFigures: KeyFigures;
  // Previous year comparative data (jämförelsetal) - ÅRL 3 kap 5§
  previousYearBalanceSheet: BalanceSheet | null;
  previousYearIncomeStatement: IncomeStatement | null;
  previousYearKeyFigures: KeyFigures | null;
  validationIssues: ValidationIssue[];
  suggestedEntries: SuggestedJournalEntry[];
  bookkeepingScenario: 1 | 2 | 3;
  // Equity change analysis - K2 5.7, ÅRL 5 kap 14§
  equityChangeAnalysis?: EquityChangeAnalysis;
}

export interface KeyFigures {
  totalAssets: number;
  equity: number;
  adjustedEquity: number;
  freeEquity: number;
  solidityPercent: number;
  netSales: number;
  netResult: number;
}

export interface ValidationIssue {
  type: "blocker" | "warning" | "info";
  code: string;
  message: string;
  legalRef?: string;
}

export interface JournalEntryLine {
  accountNumber: string;
  accountName: string;
  debit: number;
  credit: number;
}

export interface SuggestedJournalEntry {
  id: string;
  title: string;
  description: string;
  reason: string;
  lines: JournalEntryLine[];
  totalAmount: number;
  confidence: "high" | "medium";
}

// Override types for SIE data corrections
export interface SieOverrides {
  incomeStatement?: {
    revenue?: { netSales?: number; otherIncome?: number };
    expenses?: { goodsAndMaterials?: number; otherExternalCosts?: number; personnelCosts?: number; depreciationAmortization?: number; otherOperatingExpenses?: number };
    financialItems?: { financialIncome?: number; financialExpenses?: number };
    extraordinaryItems?: number;
    appropriations?: number;
    taxOnResult?: number;
  };
  balanceSheet?: {
    assets?: {
      fixedAssets?: { intangible?: number; tangible?: number; financial?: number };
      currentAssets?: { inventory?: number; receivables?: number; shortTermInvestments?: number; cashAndBank?: number };
    };
    equityAndLiabilities?: {
      equity?: { shareCapital?: number; legalReserve?: number; revaluationReserve?: number; retainedEarnings?: number };
      untaxedReserves?: number;
      provisions?: number;
      longTermLiabilities?: number;
      shortTermLiabilities?: number;
    };
  };
}

// Detaljerad anläggningstillgångsnot (ÅRL 5:8, K2 kap 10)
export interface FixedAssetNoteDetail {
  // Anskaffningsvärden
  openingAcquisitionCost: number;
  purchases: number;
  sales: number;
  businessAcquisitions?: number;
  mergers?: number;
  reclassifications?: number;
  exchangeRateDifferences?: number;

  // Avskrivningar
  openingDepreciation: number;
  yearDepreciation: number;
  salesDepreciation?: number;
  reclassificationsDepreciation?: number;
  exchangeRateDifferencesDepreciation?: number;

  // Uppskrivningar
  openingRevaluations?: number;
  yearRevaluations?: number;

  // Nedskrivningar
  openingWriteDowns?: number;
  yearWriteDowns?: number;

  // Avskrivningsmetod och nyttjandeperiod (för Not 1)
  depreciationMethod: string;
  usefulLife: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// EQUITY CHANGES USER INPUT - K2 5.7, ÅRL 5 kap 14§
// ═══════════════════════════════════════════════════════════════════════════

export interface ShareIssueInput {
  type: 'cash' | 'non_cash';
  numberOfNewShares?: number;
  subscriptionPricePerShare?: number;
  totalShareCapitalIncrease: number;
  sharePremiumAmount?: number;
  decisionDate?: string;
  registrationDate?: string;
}

export interface BonusIssueInput {
  sourceAccount: 'sharePremium' | 'legalReserve' | 'retainedEarnings';
  amount: number;
  decisionDate?: string;
}

export interface CapitalReductionInput {
  purpose: 'loss_coverage' | 'repayment_to_shareholders' | 'transfer_to_reserve';
  amount: number;
  decisionDate?: string;
  permissionFromBolagsverket?: boolean;
}

export interface ShareholderContributionInput {
  type: 'unconditional' | 'conditional';
  amount: number;
  contributorName?: string;
  conditionDescription?: string;
  dateReceived?: string;
}

export interface ContributionRepaymentInput {
  amount: number;
  recipientName?: string;
  decisionDate?: string;
}

export interface ReserveFundTransferInput {
  direction: 'to' | 'from';
  amount: number;
  reason?: string;
  decisionDate?: string;
}

export interface EquityChangesInput {
  hasReviewed?: boolean;
  shareIssue?: ShareIssueInput;
  bonusIssue?: BonusIssueInput;
  capitalReduction?: CapitalReductionInput;
  shareholderContributions?: ShareholderContributionInput[];
  contributionRepayments?: ContributionRepaymentInput[];
  reserveFundTransfers?: ReserveFundTransferInput[];
  dividendPaidDuringYear?: number;
  groupContributionReceived?: number;
  groupContributionGiven?: number;
  ownSharesPurchased?: number;
  ownSharesSold?: number;
  otherChanges?: Array<{
    description: string;
    amount: number;
    component: 'shareCapital' | 'legalReserve' | 'sharePremium' | 'retainedEarnings' | 'other';
  }>;
}

export interface UserInput {
  // Förvaltningsberättelse
  businessDescription?: string;
  significantEvents?: string;
  eventsAfterBalanceDate?: string;

  // Personal
  averageEmployees?: number;

  // Resultatdisposition
  proposedDividend?: number;

  // ═══════════════════════════════════════════════════════════════════════════
  // FÖRÄNDRINGAR I EGET KAPITAL - K2 5.7, ÅRL 5 kap 14§
  // ═══════════════════════════════════════════════════════════════════════════
  equityChanges?: EquityChangesInput;

  // Noter
  pledgedAssets?: Array<{
    type: 'corporate_mortgage' | 'property_mortgage' | 'chattel_mortgage' | 'other';
    description: string;
    amount: number;
  }>;
  contingentLiabilities?: Array<{ description: string; amount: number }>;

  // Skulder >5 år (ÅRL 5:13)
  liabilitiesOver5Years?: number;
  liabilitiesOver5YearsComment?: string;

  // Goodwill (ÅRL 5:9)
  goodwillDepreciationYears?: number;
  goodwillDepreciationReason?: string;

  // Lån till ledande befattningshavare (ÅRL 5:12)
  loansToBoard?: Array<{
    category: 'styrelse' | 'vd' | 'other';
    amount: number;
  }>;

  // Transaktioner med närstående (ÅRL 5:23, K2 18.10)
  hasRelatedPartyTransactions?: boolean;
  relatedPartyTransactions?: Array<{
    partyType?: 'owner' | 'board' | 'ceo' | 'family' | 'related_company' | 'other';
    party: string;
    description: string;
    amount: number;
  }>;

  // Moderföretag (K2 19.2)
  isSubsidiary?: boolean;
  parentCompany?: {
    name: string;
    organizationNumber: string;
    municipality: string;
  };

  // Avskrivningar för anläggningstillgångar (ÅRL 5:8, K2 10.27)
  fixedAssetDepreciation?: {
    intangible?: { method: string; usefulLife: number }; // 10xx
    buildings?: { method: string; usefulLife: number };  // 11xx
    machinery?: { method: string; usefulLife: number };  // 12xx
  };

  // Detaljerade anläggningstillgångsnot per typ (ÅRL 5:8)
  fixedAssetNotes?: {
    intangible?: FixedAssetNoteDetail;
    buildings?: FixedAssetNoteDetail;
    machinery?: FixedAssetNoteDetail;
  };

  // Tjänste-/entreprenaduppdrag (K2 kapitel 6)
  hasServiceContracts?: boolean;
  serviceContractMethod?: 'huvudregeln' | 'alternativregeln';
  serviceContractIndirectCosts?: boolean;

  // Egentillverkade varor (K2 12.8)
  hasSelfManufacturedGoods?: boolean;
  selfManufacturedGoodsIndirectCosts?: boolean;

  // Exceptionella poster (ÅRL 5:19)
  hasExceptionalItems?: boolean;
  exceptionalItems?: Array<{
    nature: string;
    amount: number;
    isIncome: boolean;
  }>;

  // Styrelse & VD
  boardApprovalDate?: string;

  // Flerårsöversikt (tidigare år om ej i SIE)
  previousYears?: Array<{ year: string; netSales: number; resultAfterFinancialItems: number; solidityPercent: number }>;

  // Dynamiskt tillagda noter
  additionalNotes?: Array<{
    noteId: string;
    title: string;
    content: string;
  }>;

  // SIE-data overrides - användaren kan korrigera värden från SIE-filen
  overrides?: SieOverrides;

  // Current wizard step - uppdateras vid navigering
  currentStep?: number;

  // K2-behörighet (2026-regler)
  k2Eligibility?: {
    hasForeignBranches?: boolean;      // Filialer i utlandet
    hasCryptoAssets?: boolean;         // Kryptotillgångar
    hasShareBasedPayments?: boolean;   // Aktierelaterade ersättningar
    confirmedAt?: string;              // När användaren bekräftade
  };

  // Manuell inmatning (utan SIE-fil)
  manualEntry?: {
    fiscalYearStart: string;
    fiscalYearEnd: string;
    createdAt: string;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // K3-SPECIFIKA FÄLT
  // ═══════════════════════════════════════════════════════════════════════════

  // Kassaflödesanalys (K3 kap 7)
  cashFlowStatement?: Partial<CashFlowStatement>;

  // Uppskjuten skatt (K3 kap 29)
  deferredTax?: {
    taxRate?: number;
    untaxedReserves?: number;
    deferredTaxLiability?: number;
    deferredTaxAsset?: number;
    taxLossCarryforwards?: number;
    taxLossCarryforwardsRecognized?: boolean;
  };

  // Koncernredovisning (K3 kap 9)
  consolidation?: {
    isParentCompany?: boolean;
    subsidiaryInputs?: Array<{
      subsidiaryId: string;
      ownershipPercent: number;
      votingRightsPercent?: number;
      acquisitionDate: string;
      acquisitionCost: number;
      goodwillUsefulLife?: number;
    }>;
  };

  // Uppladdade dotterbolags SIE-filer
  subsidiaries?: Array<{
    id: string;
    sieData: unknown;
    metadata: {
      organizationNumber: string;
      name: string;
      uploadedAt: string;
    };
  }>;

  // Nettoomsättningens fördelning (K3 8.7)
  revenueSplit?: {
    byBusinessArea?: Array<{ name: string; amount: number; percentage: number }>;
    byGeography?: Array<{ region: string; amount: number; percentage: number }>;
  };

  // Komponentavskrivning (K3 kap 17)
  componentDepreciation?: {
    enabled?: boolean;
    buildings?: Array<{
      id: string;
      name: string;
      acquisitionDate?: string;
      totalAcquisitionValue: number;
      components: Array<{
        name: string;
        percentOfTotal: number;
        usefulLife: number;
        acquisitionValue: number;
      }>;
    }>;
  };

  // Leasing (K3 kap 20)
  leasing?: {
    operationalLeases?: Array<{
      id: string;
      description: string;
      annualCost: number;
      remainingPeriod: number;
      totalCommitment: number;
    }>;
    financialLeases?: Array<{
      id: string;
      description: string;
      acquisitionValue: number;
      accumulatedDepreciation: number;
      remainingDebt: number;
      interestRate: number;
    }>;
  };

  // Jämförelsetal för noter (K3 kap 3.10) - obligatoriskt för K3
  comparativeNotes?: Record<string, number | null>;
}

export interface Calculations {
  keyFigures: KeyFigures;
  triggeredNotes: string[];
}

export interface ValidationResult {
  issues: ValidationIssue[];
  hasBlockers: boolean;
}

// ═══════════════════════════════════════════════════════════════════
// K3-SPECIFIKA TYPER
// ═══════════════════════════════════════════════════════════════════

export type AccountingStandard = "K2" | "K3";

export type CashFlowMethod = "indirect" | "direct";

export interface CashFlowManualAdjustment {
  description: string;
  amount: number;
}

export interface CashFlowOperatingActivities {
  operatingResult: number;
  adjustments?: {
    depreciation?: number;
    writeDowns?: number;
    provisions?: number;
    unrealizedExchangeDiff?: number;
    gainLossOnDisposals?: number;
    otherNonCashItems?: number;
  };
  financialItems?: {
    interestReceived?: number;
    dividendReceived?: number;
    interestPaid?: number;
  };
  taxPaid?: number;
  workingCapitalChanges?: {
    inventory?: number;
    receivables?: number;
    payables?: number;
    otherWorkingCapital?: number;
  };
  manualAdjustments?: CashFlowManualAdjustment[];
  total: number;
}

export interface CashFlowInvestingActivities {
  acquisitionOfIntangibleAssets?: number;
  disposalOfIntangibleAssets?: number;
  acquisitionOfTangibleAssets?: number;
  disposalOfTangibleAssets?: number;
  acquisitionOfFinancialAssets?: number;
  disposalOfFinancialAssets?: number;
  acquisitionOfSubsidiaries?: number;
  disposalOfSubsidiaries?: number;
  manualAdjustments?: CashFlowManualAdjustment[];
  total: number;
}

export interface CashFlowFinancingActivities {
  newShareIssue?: number;
  newLoans?: number;
  repaymentOfLoans?: number;
  dividendPaid?: number;
  groupContributions?: number;
  shareholderContributions?: number;
  manualAdjustments?: CashFlowManualAdjustment[];
  total: number;
}

export interface CashFlowSummary {
  netCashFlow: number;
  openingCash: number;
  exchangeRateDifference: number;
  closingCash: number;
}

export interface CashFlowStatement {
  method: CashFlowMethod;
  operatingActivities: CashFlowOperatingActivities;
  investingActivities: CashFlowInvestingActivities;
  financingActivities: CashFlowFinancingActivities;
  summary: CashFlowSummary;
  isBalanced: boolean;
  calculatedAt?: string;
  userAdjusted?: boolean;
}

export interface DeferredTaxManualAdjustment {
  description: string;
  amount: number;
  type: "liability" | "asset";
}

export interface DeferredTaxCalculation {
  taxRate: number;
  temporaryDifferencesLiability: {
    untaxedReserves: number;
    periodizationFunds: number;
    acceleratedDepreciation: number;
    revaluations: number;
    otherLiabilities: number;
  };
  temporaryDifferencesAsset: {
    nonDeductibleProvisions: number;
    taxLossCarryforwards: number;
    taxLossCarryforwardsRecognized: boolean;
    otherAssets: number;
  };
  deferredTaxLiability: number;
  deferredTaxAsset: number;
  netDeferredTax: number;
  changeInDeferredTax: number;
  specification: {
    untaxedReservesLiability: number;
    revaluationsLiability: number;
    provisionsAsset: number;
    taxLossAsset: number;
    manualAdjustments: DeferredTaxManualAdjustment[];
  };
  calculatedAt?: string;
  userAdjusted?: boolean;
}

export interface SubsidiaryCompany {
  id: string;
  organizationNumber: string;
  name: string;
  country: string;
  ownershipPercentage: number;
  votingRightsPercentage: number;
  acquisitionDate: string;
  acquisitionCost: number;
  equityAtAcquisition: number;
  currentEquity: number;
  currentYearResult: number;
  sieFileId?: string;
}

export interface ConsolidationElimination {
  id: string;
  type: "intercompany_receivable" | "intercompany_payable" | "intercompany_revenue" | "intercompany_expense" | "unrealized_profit" | "dividend" | "investment";
  description: string;
  debitAccount: string;
  debitAmount: number;
  creditAccount: string;
  creditAmount: number;
  affectedCompanies: string[];
}

export interface ConsolidationGoodwill {
  subsidiaryId: string;
  subsidiaryName: string;
  acquisitionCost: number;
  acquiredEquity: number;
  goodwillAmount: number;
  usefulLife: number;
  accumulatedAmortization: number;
  carryingValue: number;
  annualAmortization: number;
}

export interface MinorityInterest {
  subsidiaryId: string;
  subsidiaryName: string;
  minorityPercentage: number;
  minorityEquity: number;
  minorityShareOfResult: number;
}

export interface ConsolidatedAccounts {
  parentCompany: {
    id: string;
    organizationNumber: string;
    name: string;
  };
  subsidiaries: SubsidiaryCompany[];
  associates: Array<{
    id: string;
    organizationNumber: string;
    name: string;
    ownershipPercentage: number;
    carryingValue: number;
    shareOfResult: number;
  }>;
  eliminations: ConsolidationElimination[];
  goodwill: ConsolidationGoodwill[];
  minorityInterests: MinorityInterest[];
  consolidatedIncomeStatement: IncomeStatement;
  consolidatedBalanceSheet: BalanceSheet;
  consolidatedCashFlow: CashFlowStatement;
  totalGoodwill: number;
  totalMinorityInterest: number;
  consolidationAdjustments: number;
}

export interface ConsolidationInput {
  isParentCompany: boolean;
  subsidiaryInputs: Array<{
    subsidiaryId: string;
    ownershipPercent: number;
    votingRightsPercent?: number;
    acquisitionDate: string;
    acquisitionCost: number;
    goodwillUsefulLife?: number;
  }>;
}

export interface GoodwillItem {
  subsidiaryId: string;
  subsidiaryName: string;
  acquisitionCost: number;
  acquiredEquity: number;
  ownershipPercent: number;
  goodwill: number;
}

export interface GoodwillCalculation {
  goodwillItems: GoodwillItem[];
  totalGoodwill: number;
  message?: string;
}

export interface CreateReportData {
  companyId: string;
  fiscalYearStart: string;
  fiscalYearEnd: string;
}

// Companies API
export const companiesApi = {
  list: () => api.get<{ companies: Company[] }>("/companies"),
  get: (id: string) => api.get<{ company: CompanyWithDetails }>(`/companies/${id}`),
  create: (data: CreateCompanyData) => api.post<{ company: Company }>("/companies", data),
  update: (id: string, data: UpdateCompanyData) => api.put<{ company: Company }>(`/companies/${id}`, data),
  delete: (id: string) => api.delete<{ message: string }>(`/companies/${id}`),
  setBoardMembers: (id: string, members: Omit<BoardMember, "id">[]) =>
    api.put<{ boardMembers: BoardMember[] }>(`/companies/${id}/board`, { members }),
  getBoardMembers: (id: string) => api.get<{ boardMembers: BoardMember[] }>(`/companies/${id}/board`),
  lookup: (orgNumber: string) => api.get<CompanyLookupData>(`/companies/lookup/${encodeURIComponent(orgNumber)}`),
  updateFinancialHealthProfile: (id: string, profile: FinancialHealthProfile | null) =>
    api.patch<{ company: Company }>(`/companies/${id}/settings/financial-health-profile`, { profile }),
};

// Company Members & Invitations Types
export interface CompanyMember {
  id: string;
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: "owner" | "manager" | "editor" | "viewer";
  createdAt: string;
}

export interface CompanyInvitation {
  id: string;
  email: string;
  role: "owner" | "manager" | "editor" | "viewer";
  expiresAt: string;
  createdAt: string;
}

export interface InvitationInfo {
  id: string;
  email: string;
  role: "owner" | "manager" | "editor" | "viewer";
  expiresAt: string;
  acceptedAt: string | null;
  companyName: string;
  companyId: string;
}

// Members & Invitations API
export const membersApi = {
  list: (companyId: string) =>
    api.get<{ members: CompanyMember[] }>(`/companies/${companyId}/members`),
  changeRole: (companyId: string, memberId: string, role: string) =>
    api.patch<{ message: string }>(`/companies/${companyId}/members/${memberId}`, { role }),
  remove: (companyId: string, memberId: string) =>
    api.delete<{ message: string }>(`/companies/${companyId}/members/${memberId}`),
};

export const invitationsApi = {
  list: (companyId: string) =>
    api.get<{ invitations: CompanyInvitation[] }>(`/companies/${companyId}/invitations`),
  create: (companyId: string, data: { email: string; role: string }) =>
    api.post<{ invitation: CompanyInvitation }>(`/companies/${companyId}/invitations`, data),
  cancel: (companyId: string, invitationId: string) =>
    api.delete<{ message: string }>(`/companies/${companyId}/invitations/${invitationId}`),
  accept: (token: string) =>
    api.post<{ companyId: string; companyName: string; role: string }>("/companies/invitations/accept", { token }),
  getInfo: (token: string) =>
    api.get<{ invitation: InvitationInfo }>(`/companies/invitations/info?token=${encodeURIComponent(token)}`),
};

// Subscription Types
export interface CompanySubscription {
  id: string;
  companyId: string;
  status: "active" | "past_due" | "canceled" | "incomplete" | "trialing" | "unpaid";
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  createdAt: string;
}

// Subscriptions API
export const subscriptionsApi = {
  createCheckout: (data: { companyId: string; campaignCode?: string }) =>
    api.post<{ sessionUrl?: string; activated?: boolean }>("/subscriptions/create-checkout", data),
  getStatus: (companyId: string) =>
    api.get<{ subscription: CompanySubscription | null; isActivated: boolean }>(`/subscriptions/status/${companyId}`),
  cancel: (companyId: string) =>
    api.post<{ message: string }>(`/subscriptions/cancel/${companyId}`),
  resume: (companyId: string) =>
    api.post<{ message: string }>(`/subscriptions/resume/${companyId}`),
  customerPortal: () =>
    api.post<{ portalUrl: string }>("/subscriptions/customer-portal"),

  // Billing
  getBilling: () =>
    api.get<{ companies: BillingCompany[] }>("/subscriptions/billing"),
  getBillingHistory: () =>
    api.get<{ items: BillingHistoryItem[] }>("/subscriptions/billing/history"),
  downloadReceipt: async (subscriptionId: string): Promise<{ success: boolean; blob?: Blob; filename?: string; error?: string }> => {
    try {
      const token = api.getAccessToken();
      const response = await fetch(`${API_URL}/subscriptions/${subscriptionId}/receipt`, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error?.message || "Kunde inte generera kvitto" };
      }
      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const filename = contentDisposition?.match(/filename="(.+)"/)?.[1] || `kvitto-${subscriptionId}.pdf`;
      return { success: true, blob, filename };
    } catch {
      return { success: false, error: "Ett fel uppstod vid nedladdning" };
    }
  },


  // Verify checkout (fallback utan webhooks)
  verifyCheckout: (sessionId: string) =>
    api.post<{ activated: boolean }>("/subscriptions/verify-checkout", { sessionId }),

  // Price & Campaign validation
  getPrice: (campaignCode?: string) =>
    api.get<PriceInfo>(`/subscriptions/price${campaignCode ? `?campaignCode=${encodeURIComponent(campaignCode)}` : ""}`),
  validateCampaign: (code: string) =>
    api.post<CampaignValidation>("/subscriptions/validate-campaign", { code }),
};

// Public Config API
export interface PublicConfigCompany {
  name: string;
  orgNumber: string;
  address: string;
  postalCode: string;
  city: string;
  email: string;
}

export interface PublicConfig {
  editWindowDays: number;
  company?: PublicConfigCompany;
}

export const configApi = {
  getPublic: () => api.get<PublicConfig>("/config"),
};

// Dashboard API
export interface DashboardStats {
  totalCompanies: number;
  activeReports: number;
  completedReports: number;
}

export interface PendingAction {
  id: string;
  type: "draft_report" | "needs_activation" | "incomplete_report";
  title: string;
  description: string;
  companyId: string;
  companyName: string;
  reportId?: string;
  priority: "high" | "medium" | "low";
}

export interface UpcomingDeadline {
  id: string;
  type: "annual_report" | "subscription_renewal";
  title: string;
  description: string;
  dueDate: string;
  daysRemaining: number;
  companyId: string;
  companyName: string;
  reportId?: string;
  priority: "high" | "medium" | "low";
}

export interface RecentActivity {
  id: string;
  type: "report_exported" | "report_updated" | "company_added" | "report_created" | "sharebook_transaction" | "shareholder_added" | "protocol_created" | "protocol_finalized";
  title: string;
  description: string;
  timestamp: string;
  companyId?: string;
  companyName?: string;
  reportId?: string;
}

export interface DashboardOverview {
  pendingActions: PendingAction[];
  upcomingDeadlines: UpcomingDeadline[];
  recentActivity: RecentActivity[];
}

export const dashboardApi = {
  getStats: () => api.get<{ stats: DashboardStats }>("/dashboard/stats"),
  getOverview: () => api.get<DashboardOverview>("/dashboard/overview"),
};

// Annual Reports API
// ═══════════════════════════════════════════════════════════════════
// Admin API Types
// ═══════════════════════════════════════════════════════════════════

export interface PaginatedResult<T> {
  items: T[];
  totalItems: number;
  totalPages: number;
  page: number;
  pageSize: number;
}

export interface AdminDashboardStats {
  users: { total: number; new30Days: number };
  companies: { total: number; new30Days: number };
  reports: { total: number; byStatus: Record<string, number> };
  subscriptions: { total: number; byStatus: Record<string, number> };
  protocols?: { total: number; byStatus: Record<string, number> };
}

export interface AdminStatisticsData {
  kpis: {
    totalUsers: number;
    totalCompanies: number;
    totalReports: number;
    totalRevenue: number;
    usersTrend: number;
    companiesTrend: number;
    reportsTrend: number;
    revenueTrend: number;
  };
  revenueMetrics: {
    mrr: number;
    arr: number;
    avgRevenuePerCompany: number;
    ltv: number;
  };
  timeSeries: {
    buckets: string[];
    newUsers: number[];
    newCompanies: number[];
    newReports: number[];
    revenue: number[];
  };
  distributions: {
    companyTypes: Array<{ name: string; value: number }>;
    reportStatuses: Array<{ name: string; value: number }>;
    subscriptionStatuses: Array<{ name: string; value: number }>;
    reportFunnel: Array<{ step: string; count: number }>;
  };
  conversionMetrics: {
    companiesWithoutSubscription: number;
    totalSubscriptions: number;
    cancelledSubscriptions: number;
    churnRate: number;
    staleReports: number;
  };
  meta: {
    period: string;
    periodStart: string;
    periodEnd: string;
    bucketSize: "day" | "week" | "month";
  };
}

export interface AdminUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  emailVerified: boolean;
  createdAt: string;
  deletedAt: string | null;
  adminRoleNames: string[];
}

export interface AdminUserDetail extends AdminUser {
  companies: Array<{
    id: string;
    name: string;
    organizationNumber: string;
    role: string;
  }>;
  activeSessions: Array<{
    id: string;
    userAgent: string | null;
    ipAddress: string | null;
    createdAt: string;
    expiresAt: string;
  }>;
}

export interface AdminCompany {
  id: string;
  organizationNumber: string;
  name: string;
  type: string;
  seatMunicipality: string | null;
  city: string | null;
  createdAt: string;
  deletedAt: string | null;
  activationStatus: string;
}

export interface AdminCompanyDetail extends AdminCompany {
  members: Array<{
    userId: string;
    role: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  }>;
  board: Array<{
    id: string;
    name: string;
    role: string;
    personalId: string | null;
  }>;
  reports: Array<{
    id: string;
    fiscalYearStart: string;
    fiscalYearEnd: string;
    status: string;
    currentStep: number;
    createdAt: string;
  }>;
}

export interface AdminReport {
  id: string;
  companyId: string;
  companyName?: string;
  organizationNumber?: string;
  fiscalYearStart: string;
  fiscalYearEnd: string;
  status: string;
  currentStep: number;
  createdAt: string;
  updatedAt?: string;
  completedAt?: string | null;
  exportedAt?: string | null;
  finalizedAt?: string | null;
  deletedAt: string | null;
}

export interface AdminReportDetail extends AdminReport {
  companyName: string;
  organizationNumber: string;
  updatedAt: string;
}

export interface AdminProtocol {
  id: string;
  companyId: string;
  companyName: string;
  organizationNumber: string;
  annualReportId: string | null;
  type: "annual_meeting" | "board_meeting" | "constitutive_board";
  status: "draft" | "finalized";
  meetingDate: string;
  meetingTime: string | null;
  meetingPlace: string | null;
  chairpersonName: string | null;
  createdAt: string;
  updatedAt: string;
  finalizedAt: string | null;
}

export interface AdminProtocolDetail extends AdminProtocol {
  recordKeeperName: string | null;
  approverNames: string[];
  decisions: Record<string, unknown>;
  fiscalYearStart: string | null;
  fiscalYearEnd: string | null;
}

export interface AdminShareBook {
  id: string;
  companyId: string;
  companyName: string;
  organizationNumber: string;
  shareCapital: number;
  isLocked: boolean;
  lockedReason: string | null;
  hasPreemptionClause: boolean;
  hasConsentClause: boolean;
  hasRedemptionClause: boolean;
  hasConversionClause: boolean;
  shareholdersCount: number;
  transactionsCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminShareBookShareClass {
  id: string;
  name: string;
  code: string;
  votingPower: number;
  parValue: number | null;
  totalShares: number;
  isPreference: boolean;
}

export interface AdminShareBookShareholder {
  id: string;
  type: "person" | "company" | "estate" | "trust";
  name: string;
  personalId: string | null;
  orgNumber: string | null;
  email: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface AdminShareBookTransaction {
  id: string;
  type: string;
  quantity: number;
  transactionDate: string;
  totalAmount: number | null;
  notes: string | null;
  registeredAt: string;
  fromShareholderName: string | null;
  toShareholderName: string | null;
}

export interface AdminShareBookDetail extends AdminShareBook {
  clauseDetails: Record<string, unknown>;
  shareClasses: AdminShareBookShareClass[];
  shareholders: AdminShareBookShareholder[];
  recentTransactions: AdminShareBookTransaction[];
  totalShares: number;
  activeShareholders: number;
  totalShareholders: number;
}

export interface AdminShareBookStats {
  totalShareBooks: number;
  lockedShareBooks: number;
  totalShareholders: number;
  activeShareholders: number;
  totalTransactions: number;
  totalShareCapital: number;
}

export interface AdminAuditLog {
  id: string;
  userId: string | null;
  userEmail?: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  oldValues: unknown;
  newValues: unknown;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface AdminSubscription {
  id: string;
  companyId: string;
  companyName: string;
  organizationNumber: string;
  userId: string | null;
  userEmail: string | null;
  stripeSubscriptionId: string;
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  createdAt: string;
}

export interface AdminSubscriptionDetail extends AdminSubscription {
  stripeCustomerId: string | null;
  stripePriceId: string | null;
  userName: string | null;
  companyAddress: string | null;
  companyPostalCode: string | null;
  companyCity: string | null;
  updatedAt: string;
  amount: string | null;
  currency: string | null;
  discountAmount: string | null;
  campaignCode: string | null;
}

// Billing types (customer-facing)
export interface BillingCompany {
  companyId: string;
  companyName: string;
  organizationNumber: string;
  billingStatus: "active_subscription" | "canceled_subscription" | "past_due" | "inactive";
  subscription: {
    id: string;
    status: string;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    canceledAt: string | null;
  } | null;
}

export interface BillingHistoryItem {
  id: string;
  companyId: string;
  companyName: string;
  type: "subscription";
  description: string;
  status: string;
  amount?: string | null;
  currency?: string | null;
  date: string;
}

export interface SystemSetting {
  key: string;
  value: unknown;
  description: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface AdminRole {
  id: string;
  name: string;
  description: string | null;
  permissions: Record<string, string[]>;
  userCount: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface AdminRoleDetail extends Omit<AdminRole, "userCount"> {
  assignedUsers: Array<{
    userId: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    role: string;
    grantedAt: string;
    grantedBy: string | null;
  }>;
}

export interface UserAdminRole {
  roleId: string;
  roleName: string;
  roleDescription: string | null;
  grantedAt: string;
  grantedBy: string | null;
}

export interface UserPermissions {
  permissions: Record<string, string[]>;
  isSuperAdmin: boolean;
}

export interface CompanyRoleStats {
  totalMemberships: number;
  byRole: Record<string, number>;
  uniqueUsers: number;
  uniqueCompanies: number;
}

export interface AdminListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  includeDeleted?: boolean;
}

function buildQueryString(params: object): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (entries.length === 0) return "";
  return "?" + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join("&");
}

// Admin API
export const adminApi = {
  // Dashboard
  getDashboardStats: () =>
    api.get<AdminDashboardStats>("/admin/dashboard"),

  // Statistics
  getStatistics: (period: string = "30d", customRange?: { from: string; to: string }) => {
    const params = new URLSearchParams({ period });
    if (period === "custom" && customRange) {
      params.set("from", customRange.from);
      params.set("to", customRange.to);
    }
    return api.get<AdminStatisticsData>(`/admin/statistics?${params.toString()}`);
  },

  // Users
  listUsers: (params: AdminListParams & { role?: string; emailVerified?: string; createdAfter?: string; createdBefore?: string } = {}) =>
    api.get<PaginatedResult<AdminUser>>(`/admin/users${buildQueryString(params)}`),
  getUser: (id: string) =>
    api.get<AdminUserDetail>(`/admin/users/${id}`),
  updateUser: (id: string, data: { email?: string; firstName?: string; lastName?: string; role?: string; emailVerified?: boolean }) =>
    api.patch<AdminUser>(`/admin/users/${id}`, data),
  deleteUser: (id: string) =>
    api.delete<{ message: string }>(`/admin/users/${id}`),
  restoreUser: (id: string) =>
    api.post<AdminUser>(`/admin/users/${id}/restore`),
  resetUserPassword: (id: string, newPassword: string) =>
    api.post<{ message: string }>(`/admin/users/${id}/reset-password`, { newPassword }),

  // Companies
  listCompanies: (params: AdminListParams & { type?: string; city?: string; activationStatus?: string; createdAfter?: string; createdBefore?: string } = {}) =>
    api.get<PaginatedResult<AdminCompany>>(`/admin/companies${buildQueryString(params)}`),
  getCompany: (id: string) =>
    api.get<AdminCompanyDetail>(`/admin/companies/${id}`),
  updateCompany: (id: string, data: { name?: string; organizationNumber?: string; type?: string; seatMunicipality?: string }) =>
    api.patch<AdminCompany>(`/admin/companies/${id}`, data),
  deleteCompany: (id: string) =>
    api.delete<{ message: string }>(`/admin/companies/${id}`),
  restoreCompany: (id: string) =>
    api.post<AdminCompany>(`/admin/companies/${id}/restore`),

  // Reports
  listReports: (params: AdminListParams & { status?: string; companyId?: string; includeDeleted?: boolean; fiscalYear?: string; currentStep?: string; createdAfter?: string; createdBefore?: string } = {}) =>
    api.get<PaginatedResult<AdminReport>>(`/admin/reports${buildQueryString(params)}`),
  getReport: (id: string) =>
    api.get<AdminReportDetail>(`/admin/reports/${id}`),
  updateReport: (id: string, data: { status?: string; completedAt?: string | null; exportedAt?: string | null; finalizedAt?: string | null }) =>
    api.patch<AdminReport>(`/admin/reports/${id}`, data),
  deleteReport: (id: string) =>
    api.delete<{ message: string }>(`/admin/reports/${id}`),
  restoreReport: (id: string) =>
    api.post<AdminReport>(`/admin/reports/${id}/restore`),
  downloadReportXbrl: async (id: string): Promise<{ success: boolean; blob?: Blob; filename?: string; error?: string }> => {
    try {
      const token = api.getAccessToken();
      const response = await fetch(`${API_URL}/admin/reports/${id}/xbrl`, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error?.message || "Kunde inte generera iXBRL" };
      }
      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const filename = contentDisposition?.match(/filename="(.+)"/)?.[1] || `arsredovisning-${id}.xhtml`;
      return { success: true, blob, filename };
    } catch {
      return { success: false, error: "Ett fel uppstod vid generering av iXBRL" };
    }
  },

  // Share Books (Aktieböcker)
  listShareBooks: (params: AdminListParams & { companyId?: string; isLocked?: boolean } = {}) =>
    api.get<PaginatedResult<AdminShareBook>>(`/admin/share-books${buildQueryString(params)}`),
  getShareBook: (id: string) =>
    api.get<AdminShareBookDetail>(`/admin/share-books/${id}`),
  updateShareBook: (id: string, data: { isLocked?: boolean; lockedReason?: string | null }) =>
    api.patch<AdminShareBook>(`/admin/share-books/${id}`, data),
  deleteShareBook: (id: string) =>
    api.delete<{ message: string }>(`/admin/share-books/${id}`),
  getShareBookStats: () =>
    api.get<AdminShareBookStats>("/admin/share-books/stats"),

  // Protocols (Protokoll)
  listProtocols: (params: AdminListParams & { status?: string; companyId?: string; type?: string; meetingDateFrom?: string; meetingDateTo?: string } = {}) =>
    api.get<PaginatedResult<AdminProtocol>>(`/admin/protocols${buildQueryString(params)}`),
  getProtocol: (id: string) =>
    api.get<AdminProtocolDetail>(`/admin/protocols/${id}`),
  updateProtocol: (id: string, data: { meetingDate?: string; meetingTime?: string; meetingPlace?: string; chairpersonName?: string; recordKeeperName?: string; approverNames?: string[] }) =>
    api.patch<AdminProtocol>(`/admin/protocols/${id}`, data),
  deleteProtocol: (id: string) =>
    api.delete<{ message: string }>(`/admin/protocols/${id}`),
  unfinalizeProtocol: (id: string) =>
    api.post<AdminProtocol>(`/admin/protocols/${id}/unfinalize`),

  // Subscriptions
  listSubscriptions: (params: AdminListParams & { status?: string; userEmail?: string; periodStart?: string; createdAfter?: string; createdBefore?: string } = {}) =>
    api.get<PaginatedResult<AdminSubscription>>(`/admin/subscriptions${buildQueryString(params)}`),
  getSubscription: (id: string) =>
    api.get<AdminSubscriptionDetail>(`/admin/subscriptions/${id}`),
  downloadSubscriptionReceipt: async (id: string): Promise<{ success: boolean; blob?: Blob; filename?: string; error?: string }> => {
    try {
      const token = api.getAccessToken();
      const response = await fetch(`${API_URL}/admin/subscriptions/${id}/receipt`, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error?.message || "Kunde inte generera kvitto" };
      }
      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const filename = contentDisposition?.match(/filename="(.+)"/)?.[1] || `kvitto-${id}.pdf`;
      return { success: true, blob, filename };
    } catch {
      return { success: false, error: "Ett fel uppstod vid nedladdning" };
    }
  },

  // Audit Logs
  listAuditLogs: (params: AdminListParams & { userId?: string; action?: string; entityType?: string; ipAddress?: string; createdAfter?: string; createdBefore?: string } = {}) =>
    api.get<PaginatedResult<AdminAuditLog>>(`/admin/audit-logs${buildQueryString(params)}`),

  // Settings
  getSettings: () =>
    api.get<SystemSetting[]>("/admin/settings"),
  updateSetting: (key: string, value: unknown, description?: string) =>
    api.put<SystemSetting>("/admin/settings", { key, value, description }),

  // Roles
  listRoles: (params: { includeDeleted?: boolean } = {}) =>
    api.get<AdminRole[]>(`/admin/roles${buildQueryString(params)}`),
  getRole: (id: string) =>
    api.get<AdminRoleDetail>(`/admin/roles/${id}`),
  createRole: (data: { name: string; description?: string; permissions: Record<string, string[]> }) =>
    api.post<AdminRole>("/admin/roles", data),
  updateRole: (id: string, data: { name?: string; description?: string; permissions?: Record<string, string[]> }) =>
    api.patch<AdminRole>(`/admin/roles/${id}`, data),
  deleteRole: (id: string) =>
    api.delete<{ message: string }>(`/admin/roles/${id}`),
  restoreRole: (id: string) =>
    api.post<AdminRole>(`/admin/roles/${id}/restore`),

  // User Role Assignments
  assignRoleToUser: (userId: string, roleId: string) =>
    api.post<unknown>(`/admin/users/${userId}/roles`, { roleId }),
  removeRoleFromUser: (userId: string, roleId: string) =>
    api.delete<{ message: string }>(`/admin/users/${userId}/roles/${roleId}`),
  getUserRoles: (userId: string) =>
    api.get<UserAdminRole[]>(`/admin/users/${userId}/roles`),

  // Permissions
  getMyPermissions: () =>
    api.get<UserPermissions>("/admin/me/permissions"),

  // Company Roles
  getCompanyRoleStats: () =>
    api.get<CompanyRoleStats>("/admin/company-roles/stats"),

  // Impersonation
  impersonateUser: (userId: string) =>
    api.post<ImpersonationResponse>(`/admin/users/${userId}/impersonate`),

  // Cleanup
  cleanupSoftDeleted: () =>
    api.post<CleanupResult>("/admin/cleanup"),

  // IP Blocking
  getBlockedIps: () =>
    api.get<BlockedIp[]>("/admin/blocked-ips"),
  unblockIp: (ip: string) =>
    api.delete<{ message: string }>(`/admin/blocked-ips/${encodeURIComponent(ip)}`),
  getIpFailures: (ip: string) =>
    api.get<{ ip: string; failureCount: number }>(`/admin/ip-failures/${encodeURIComponent(ip)}`),

  // Job Logs (Cron/Queue)
  listJobLogs: (params: AdminListParams & { jobType?: string; status?: string; createdAfter?: string } = {}) =>
    api.get<PaginatedResult<JobLog>>(`/admin/job-logs${buildQueryString(params)}`),
};

// Cleanup result type
export interface CleanupResult {
  users: number;
  companies: number;
  reports: number;
  roles: number;
  total: number;
  retentionDays: number;
  cutoffDate: string;
}

// Blocked IP type
export interface BlockedIp {
  ip: string;
  remainingSeconds: number;
}

// Job Log type
export interface JobLog {
  id: string;
  jobType: string;
  jobId: string | null;
  status: "started" | "completed" | "failed";
  inputData: unknown;
  outputData: unknown;
  errorMessage: string | null;
  durationMs: number | null;
  createdAt: string;
}

// Impersonation types
export interface ImpersonationResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    role: string;
  };
  impersonation: {
    active: boolean;
    impersonatedBy: string;
    impersonatorEmail: string;
  };
}

// Payments API
export interface PriceInfo {
  basePrice: number;
  vatRate: number;
  discountPercent: number;
  discountAmount: number;
  priceExclVat: number;
  priceInclVat: number;
  vatAmount: number;
  totalPrice: number;
  campaignCode?: string;
  subscriptionDescription: string;
}

export interface CampaignValidation {
  valid: boolean;
  discountPercent?: number;
  code?: string;
  message?: string;
  duration?: "forever" | "repeating";
  durationInMonths?: number | null;
}

// Annual Reports API
export const reportsApi = {
  list: (companyId: string) => api.get<{ reports: AnnualReportSummary[] }>(`/reports?companyId=${companyId}`),
  get: (id: string) => api.get<{ report: AnnualReport }>(`/reports/${id}`),
  create: (data: CreateReportData) => api.post<{ report: AnnualReport }>("/reports", data),
  delete: (id: string) => api.delete<{ message: string }>(`/reports/${id}`),

  // Create report with SIE file in one step (recommended)
  createWithSie: async (companyId: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("companyId", companyId);

    const token = api.getAccessToken();
    const response = await fetch(`${API_URL}/reports/create-with-sie`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
      credentials: "include",
    });

    return response.json() as Promise<ApiResponse<{ report: AnnualReport; parsedData: ParsedSieData; triggeredNotes: string[] }>>;
  },

  // Upload SIE to existing report (legacy)
  uploadSie: async (id: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    const token = api.getAccessToken();
    const response = await fetch(`${API_URL}/reports/${id}/upload-sie`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
      credentials: "include",
    });

    return response.json() as Promise<ApiResponse<{ report: AnnualReport; parsedData: ParsedSieData; triggeredNotes: string[] }>>;
  },
  updateInput: (id: string, input: Partial<UserInput>) =>
    api.put<{ report: AnnualReport }>(`/reports/${id}/input`, input),
  updateStep: (id: string, step: number) =>
    api.put<{ report: AnnualReport }>(`/reports/${id}/step`, { step }),
  updateStandard: (id: string, accountingStandard: "K2" | "K3") =>
    api.put<{ report: AnnualReport }>(`/reports/${id}/standard`, { accountingStandard }),
  // Save manually created ParsedSieData (for manual entry flow)
  saveManualSieData: (id: string, parsedSieData: ParsedSieData) =>
    api.put<{ report: AnnualReport }>(`/reports/${id}/manual-sie-data`, { parsedSieData }),
  downloadPdf: async (id: string): Promise<{ success: boolean; blob?: Blob; filename?: string; error?: string }> => {
    try {
      const token = api.getAccessToken();
      const response = await fetch(`${API_URL}/reports/${id}/pdf`, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error?.message || "Kunde inte generera PDF" };
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const filename = contentDisposition?.match(/filename="(.+)"/)?.[1] || "arsredovisning.pdf";

      return { success: true, blob, filename };
    } catch (error) {
      return { success: false, error: "Ett fel uppstod vid nedladdning" };
    }
  },

  downloadXbrl: async (id: string): Promise<{ success: boolean; blob?: Blob; filename?: string; error?: string }> => {
    try {
      const token = api.getAccessToken();
      const response = await fetch(`${API_URL}/reports/${id}/xbrl`, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error?.message || "Kunde inte generera iXBRL" };
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const filename = contentDisposition?.match(/filename="(.+)"/)?.[1] || "arsredovisning.xhtml";

      return { success: true, blob, filename };
    } catch (error) {
      return { success: false, error: "Ett fel uppstod vid generering av iXBRL" };
    }
  },

  // Update report status
  updateStatus: (id: string, status: AnnualReportSummary["status"]) =>
    api.put<{ report: AnnualReport }>(`/reports/${id}/status`, { status }),

  // Finalize report and submit to Bolagsverket
  finalize: (id: string) =>
    api.post<{ report: AnnualReport; submissionId?: string }>(`/reports/${id}/finalize`),

  // ═══════════════════════════════════════════════════════════════════
  // K3-SPECIFIKA ENDPOINTS
  // ═══════════════════════════════════════════════════════════════════

  // Kassaflödesanalys (Cash Flow Statement)
  getCashFlow: (id: string) =>
    api.get<{ cashFlow: CashFlowStatement }>(`/reports/${id}/cash-flow`),
  updateCashFlow: (id: string, cashFlow: Partial<CashFlowStatement>) =>
    api.post<{ cashFlow: CashFlowStatement; report: AnnualReport }>(`/reports/${id}/cash-flow`, cashFlow),

  // Uppskjuten skatt (Deferred Tax)
  getDeferredTax: (id: string) =>
    api.get<{ deferredTax: DeferredTaxCalculation }>(`/reports/${id}/deferred-tax`),
  updateDeferredTax: (id: string, deferredTax: Partial<DeferredTaxCalculation>) =>
    api.post<{ deferredTax: DeferredTaxCalculation; report: AnnualReport }>(`/reports/${id}/deferred-tax`, deferredTax),

  // Koncernredovisning (Consolidation)
  getConsolidation: (id: string) =>
    api.get<{ consolidation: ConsolidatedAccounts | null; report: AnnualReport }>(`/reports/${id}/consolidation`),
  updateConsolidation: (id: string, consolidation: ConsolidationInput) =>
    api.post<{ consolidation: ConsolidatedAccounts; report: AnnualReport }>(`/reports/${id}/consolidation`, consolidation),
  addSubsidiary: async (id: string, file: File): Promise<ApiResponse<{ report: AnnualReport; subsidiaryId: string }>> => {
    const formData = new FormData();
    formData.append("file", file);
    const token = api.getAccessToken();
    const response = await fetch(`${API_URL}/reports/${id}/consolidation/subsidiary`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
      body: formData,
    });
    return response.json() as Promise<ApiResponse<{ report: AnnualReport; subsidiaryId: string }>>;
  },
  removeSubsidiary: (id: string, subsidiaryId: string) =>
    api.delete<{ report: AnnualReport }>(`/reports/${id}/consolidation/subsidiary/${subsidiaryId}`),
  validateConsolidation: (id: string) =>
    api.get<{ valid: boolean; errors: string[]; warnings: string[] }>(`/reports/${id}/consolidation/validate`),
  getGoodwillCalculation: (id: string) =>
    api.get<{ goodwill: GoodwillCalculation }>(`/reports/${id}/consolidation/goodwill`),
};

// ═══════════════════════════════════════════════════════════════════
// Notification Types
// ═══════════════════════════════════════════════════════════════════

export type NotificationType =
  // Subscription & Payment
  | "subscription_renewed"
  | "subscription_expiring"
  | "payment_received"
  | "payment_failed"
  | "payment_retry_scheduled"
  | "free_year_ending"
  // Annual Reports
  | "report_exported"
  | "annual_report_created"
  | "annual_report_status_changed"
  | "annual_report_exported"
  // Share Book
  | "share_book_created"
  | "share_book_exported"
  | "capital_change"
  | "shareholder_added"
  | "shareholder_updated"
  | "shareholders_imported"
  | "pledge_added"
  | "pledge_removed"
  | "transaction_registered"
  // Other
  | "company_invitation"
  | "system_announcement";

export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string | null;
  data: Record<string, unknown>;
  actionUrl: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationPreferences {
  emailSubscriptionReminders: boolean;
  emailPaymentReceipts: boolean;
  emailPaymentFailures: boolean;
  emailMarketing: boolean;
  inAppEnabled: boolean;
}

// Notifications API
export const notificationsApi = {
  list: (params: { limit?: number; offset?: number; unreadOnly?: boolean } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.set("limit", String(params.limit));
    if (params.offset) queryParams.set("offset", String(params.offset));
    if (params.unreadOnly) queryParams.set("unreadOnly", "true");
    const query = queryParams.toString();
    return api.get<{ notifications: Notification[]; total: number }>(
      `/notifications${query ? `?${query}` : ""}`
    );
  },

  getUnreadCount: () =>
    api.get<{ count: number }>("/notifications/unread-count"),

  markAsRead: (id: string) =>
    api.patch<{ success: boolean }>(`/notifications/${id}/read`),

  markAllAsRead: () =>
    api.patch<{ count: number }>("/notifications/read-all"),

  delete: (id: string) =>
    api.delete<{ success: boolean }>(`/notifications/${id}`),

  getPreferences: () =>
    api.get<NotificationPreferences>("/notifications/preferences"),

  updatePreferences: (updates: Partial<NotificationPreferences>) =>
    api.patch<NotificationPreferences>("/notifications/preferences", updates),
};

// Public Wizard API (no auth for parse-sie)
export const wizardApi = {
  parseSie: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${API_URL}/wizard/parse-sie`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      return data as ApiResponse<{
        parsedSieData: ParsedSieData;
        calculations: Calculations;
        companyInfo: { name: string; organizationNumber: string };
        fiscalYear: { start: string; end: string };
      }>;
    } catch {
      return {
        success: false as const,
        error: { code: "NETWORK_ERROR", message: "Kunde inte ansluta till servern" },
      };
    }
  },

  convert: (data: {
    parsedSieData: ParsedSieData;
    userInput: Record<string, unknown>;
    boardMembers?: Array<{ name: string; role: string; personalId?: string | null }>;
  }) =>
    api.post<{ reportId: string; companyId: string; existing: boolean }>("/wizard/convert", data),

  /** Calculate cash flow from SIE data (no auth required, for K3 guest wizard) */
  calculateCashFlow: async (data: {
    parsedSieData: ParsedSieData;
    userInput?: Record<string, unknown>;
  }) => {
    try {
      const response = await fetch(`${API_URL}/wizard/calculate-cash-flow`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();
      return result as ApiResponse<{
        cashFlow: CashFlowStatement;
        source: "calculated" | "saved";
        message?: string;
      }>;
    } catch {
      return {
        success: false as const,
        error: { code: "NETWORK_ERROR", message: "Kunde inte ansluta till servern" },
      };
    }
  },

  /** Public company lookup via Bolagsverket (no auth required) */
  lookupCompany: async (orgNumber: string) => {
    try {
      const response = await fetch(`${API_URL}/wizard/lookup/${encodeURIComponent(orgNumber)}`, {
        method: "GET",
      });

      const data = await response.json();
      return data as ApiResponse<{
        organizationNumber: string;
        name: string;
        type: "AB" | "EF" | "HB" | "KB";
        seatMunicipality?: string;
        address?: {
          street?: string;
          postalCode?: string;
          city?: string;
        };
        shareCapital?: number;
        businessDescription?: string;
      }>;
    } catch {
      return {
        success: false as const,
        error: { code: "NETWORK_ERROR", message: "Kunde inte ansluta till servern" },
      };
    }
  },
};

// ═══════════════════════════════════════════════════════════════════
// Share Book Types (Digital aktiebok - ABL 5 kap)
// ═══════════════════════════════════════════════════════════════════

export type ShareholderType = "person" | "company" | "estate" | "trust";

export type TransactionType =
  | "founding"
  | "new_issue"
  | "bonus_issue"
  | "split"
  | "reverse_split"
  | "reduction"
  | "purchase"
  | "gift"
  | "inheritance"
  | "estate_division"
  | "conversion"
  | "redemption"
  | "merger"
  | "demerger"
  | "pledge"
  | "unpledge"
  | "correction";

export type CapitalChangeType =
  | "founding"
  | "new_issue"
  | "bonus_issue"
  | "reduction"
  | "redemption";

export type DecisionOrgan = "agm" | "egm" | "board";

// Share Book (Aktiebok)
export interface ShareBook {
  id: string;
  companyId: string;
  shareCapital: string;
  hasPreemptionClause: boolean;
  hasConsentClause: boolean;
  hasRedemptionClause: boolean;
  hasConversionClause: boolean;
  clauseDetails: Record<string, unknown>;
  isLocked: boolean;
  lockedReason: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ShareBookSummary extends ShareBook {
  companyName: string;
  organizationNumber: string;
  totalShareholders: number;
  totalShares: number;
  totalVotes: number;
  shareClasses: ShareClass[];
}

export interface CreateShareBookData {
  shareCapital: number;
  hasPreemptionClause?: boolean;
  hasConsentClause?: boolean;
  hasRedemptionClause?: boolean;
  hasConversionClause?: boolean;
  clauseDetails?: Record<string, unknown>;
}

export interface UpdateShareBookData {
  shareCapital?: number;
  hasPreemptionClause?: boolean;
  hasConsentClause?: boolean;
  hasRedemptionClause?: boolean;
  hasConversionClause?: boolean;
  clauseDetails?: Record<string, unknown>;
}

// Share Class (Aktieslag)
export interface ShareClass {
  id: string;
  shareBookId: string;
  name: string;
  code: string;
  votingPower: string;
  parValue: string | null;
  isPreference: boolean;
  preferenceDividend: string | null;
  preferenceDetails: string | null;
  totalShares: number;
  numberSeriesStart: number;
  numberSeriesCurrent: number;
  displayOrder: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateShareClassData {
  name: string;
  code: string;
  votingPower?: number;
  parValue?: number;
  isPreference?: boolean;
  preferenceDividend?: number;
  preferenceDetails?: string;
  totalShares?: number;
  numberSeriesStart?: number;
  displayOrder?: number;
}

export interface UpdateShareClassData {
  name?: string;
  votingPower?: number;
  parValue?: number;
  isPreference?: boolean;
  preferenceDividend?: number;
  preferenceDetails?: string;
  displayOrder?: number;
}

// Shareholder (Aktieägare)
export interface Shareholder {
  id: string;
  shareBookId: string;
  type: ShareholderType;
  name: string;
  personalId: string | null;
  orgNumber: string | null;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  email: string | null;
  phone: string | null;
  hidePersonalId: boolean;
  hasGuardian: boolean;
  guardianName: string | null;
  guardianPersonalId: string | null;
  guardianAddress: string | null;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ShareholderHoldingSummary {
  shareClass: ShareClass;
  totalShares: number;
  totalVotes: number;
  shareNumbers: string;
}

export interface ShareholderWithHoldings extends Shareholder {
  holdings: ShareholderHoldingSummary[];
  totalShares: number;
  totalVotes: number;
  ownershipPercent: number;
  votingPercent: number;
}

export interface CreateShareholderData {
  type: ShareholderType;
  name: string;
  personalId?: string;
  orgNumber?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  email?: string;
  phone?: string;
  hidePersonalId?: boolean;
  hasGuardian?: boolean;
  guardianName?: string;
  guardianPersonalId?: string;
  guardianAddress?: string;
  notes?: string;
}

export interface UpdateShareholderData {
  name?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  email?: string;
  phone?: string;
  hidePersonalId?: boolean;
  hasGuardian?: boolean;
  guardianName?: string;
  guardianPersonalId?: string;
  guardianAddress?: string;
  notes?: string;
}

// Share Holding (Aktieinnehav)
export interface ShareHolding {
  id: string;
  shareholderId: string;
  shareClassId: string;
  numberFrom: number;
  numberTo: number;
  acquiredAt: string;
  acquisitionType: TransactionType;
  acquisitionTransactionId: string | null;
  isPledged: boolean;
  pledgedTo: string | null;
  pledgeDate: string | null;
  pledgeNotes: string | null;
  isValid: boolean;
  invalidatedAt: string | null;
  invalidatedByTransactionId: string | null;
  createdAt: string;
  updatedAt: string;
}

// Share Transaction (Transaktion)
export interface ShareTransaction {
  id: string;
  shareBookId: string;
  type: TransactionType;
  fromShareholderId: string | null;
  toShareholderId: string | null;
  shareClassId: string | null;
  quantity: number;
  numberFrom: number | null;
  numberTo: number | null;
  pricePerShare: string | null;
  totalAmount: string | null;
  currency: string | null;
  transactionDate: string;
  registeredAt: string;
  registeredBy: string | null;
  notes: string | null;
  internalNotes: string | null;
  documents: TransactionDocument[];
  isCorrection: boolean;
  correctsTransactionId: string | null;
  correctionReason: string | null;
  isVerified: boolean;
  verifiedAt: string | null;
  verifiedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TransactionDocument {
  name: string;
  path: string;
  uploadedAt: string;
  mimeType?: string;
  size?: number;
}

// Transaction input types
export interface FoundingTransactionData {
  shareClassId: string;
  toShareholderId: string;
  quantity: number;
  pricePerShare: number;
  transactionDate: string;
  notes?: string;
}

export interface PurchaseTransactionData {
  shareClassId: string;
  fromShareholderId: string;
  toShareholderId: string;
  numberFrom: number;
  numberTo: number;
  pricePerShare: number;
  transactionDate: string;
  preemptionWaived?: boolean;
  consentObtained?: boolean;
  notes?: string;
}

export interface GiftTransactionData {
  shareClassId: string;
  fromShareholderId: string;
  toShareholderId: string;
  numberFrom: number;
  numberTo: number;
  transactionDate: string;
  preemptionWaived?: boolean;
  consentObtained?: boolean;
  notes?: string;
}

export interface InheritanceTransactionData {
  shareClassId: string;
  fromShareholderId: string;
  toShareholderId: string;
  numberFrom: number;
  numberTo: number;
  transactionDate: string;
  deceasedDate?: string;
  estateInventoryDate?: string;
  notes?: string;
}

export interface NewIssueTransactionData {
  shareClassId: string;
  toShareholderId: string;
  quantity: number;
  pricePerShare: number;
  transactionDate: string;
  registrationDate?: string;
  notes?: string;
}

export interface BonusIssueTransactionData {
  shareClassId: string;
  ratio: number; // e.g., 2 means 2 new shares per existing share
  recordDate: string; // Avstämningsdag
  transactionDate: string;
  notes?: string;
}

export interface SplitTransactionData {
  shareClassId?: string; // If null, applies to all share classes
  ratio: number; // e.g., 2 means each share becomes 2 shares
  transactionDate: string;
  notes?: string;
}

export interface ReverseSplitTransactionData {
  shareClassId?: string; // If null, applies to all share classes
  ratio: number; // e.g., 10 means 10 shares become 1 share
  transactionDate: string;
  notes?: string;
}

export interface ReductionTransactionData {
  shareClassId: string;
  shareholderId: string;
  quantity: number;
  numberFrom: number;
  numberTo: number;
  reductionReason: "loss_coverage" | "shareholder_repayment" | "reserve_fund";
  amountPerShare: number;
  transactionDate: string;
  notes?: string;
}

export interface ConversionTransactionData {
  shareholderId: string;
  fromShareClassId: string;
  toShareClassId: string;
  quantity: number;
  numberFrom: number;
  numberTo: number;
  transactionDate: string;
  notes?: string;
}

export interface RedemptionTransactionData {
  shareholderId: string;
  shareClassId: string;
  quantity: number;
  numberFrom: number;
  numberTo: number;
  pricePerShare: number;
  transactionDate: string;
  notes?: string;
}

export interface PledgeTransactionData {
  shareholderId: string;
  shareClassId: string;
  numberFrom: number;
  numberTo: number;
  pledgedTo: string;
  transactionDate: string;
  notes?: string;
}

export interface UnpledgeTransactionData {
  shareholderId: string;
  shareClassId: string;
  numberFrom: number;
  numberTo: number;
  transactionDate: string;
  notes?: string;
}

export interface CorrectionTransactionData {
  correctsTransactionId: string;
  correctionReason: string;
  transactionDate: string;
  internalNotes?: string;
}

export interface BulkImportResult {
  total: number;
  created: number;
  skipped: number;
  errors: Array<{
    row: number;
    name: string;
    error: string;
  }>;
  createdShareholders: Array<{
    id: string;
    name: string;
    type: ShareholderType;
  }>;
}

// Share Book Event (Händelse för audit trail)
export interface ShareBookEvent {
  id: string;
  shareBookId: string;
  eventType: string;
  eventData: Record<string, unknown>;
  previousData: Record<string, unknown> | null;
  entityType: string;
  entityId: string;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  checksum: string | null;
  createdAt: string;
}

// Share Capital Change (Aktiekapitalförändring)
export interface ShareCapitalChange {
  id: string;
  shareBookId: string;
  transactionId: string | null;
  changeType: CapitalChangeType;
  previousShareCapital: string;
  newShareCapital: string;
  previousTotalShares: number;
  newTotalShares: number;
  registrationDate: string | null;
  registrationNumber: string | null;
  decisionDate: string;
  effectiveDate: string | null;
  decidedBy: DecisionOrgan;
  createdAt: string;
}

// ═══════════════════════════════════════════════════════════════════
// Share Book API
// ═══════════════════════════════════════════════════════════════════

export const shareBookApi = {
  // Share Book CRUD
  get: (companyId: string) =>
    api.get<ShareBookSummary | null>(`/companies/${companyId}/share-book`),

  create: (companyId: string, data: CreateShareBookData) =>
    api.post<ShareBook>(`/companies/${companyId}/share-book`, data),

  update: (companyId: string, data: UpdateShareBookData) =>
    api.patch<ShareBook>(`/companies/${companyId}/share-book`, data),

  delete: (companyId: string, options?: { force?: boolean }) =>
    api.delete<{ success: boolean; message: string }>(
      `/companies/${companyId}/share-book${options?.force ? "?force=true" : ""}`
    ),

  // Shareholders summary
  getShareholdersSummary: (companyId: string) =>
    api.get<ShareholderWithHoldings[]>(`/companies/${companyId}/share-book/shareholders-summary`),

  // Recent transactions
  getRecentTransactions: (companyId: string, limit?: number) =>
    api.get<ShareTransaction[]>(
      `/companies/${companyId}/share-book/recent-transactions${limit ? `?limit=${limit}` : ""}`
    ),

  // Export PDF
  exportPdf: async (companyId: string): Promise<Blob> => {
    const headers: HeadersInit = {};
    const accessToken = api.getAccessToken();
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${API_URL}/companies/${companyId}/share-book/export-pdf`, {
      method: "GET",
      headers,
      credentials: "include",
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: "Ett fel uppstod" } }));
      throw new Error(error.error?.message || "Kunde inte exportera PDF");
    }
    return response.blob();
  },

  // Share Classes
  shareClasses: {
    list: (companyId: string) =>
      api.get<ShareClass[]>(`/companies/${companyId}/share-classes`),

    get: (shareClassId: string) =>
      api.get<ShareClass>(`/share-classes/${shareClassId}`),

    create: (companyId: string, data: CreateShareClassData) =>
      api.post<ShareClass>(`/companies/${companyId}/share-classes`, data),

    update: (shareClassId: string, data: UpdateShareClassData) =>
      api.patch<ShareClass>(`/share-classes/${shareClassId}`, data),

    delete: (shareClassId: string) =>
      api.delete<{ success: boolean }>(`/share-classes/${shareClassId}`),
  },

  // Shareholders
  shareholders: {
    list: (companyId: string, params?: { includeInactive?: boolean; search?: string }) => {
      const queryParams = new URLSearchParams();
      if (params?.includeInactive) queryParams.set("includeInactive", "true");
      if (params?.search) queryParams.set("search", params.search);
      const query = queryParams.toString();
      return api.get<Shareholder[]>(`/companies/${companyId}/shareholders${query ? `?${query}` : ""}`);
    },

    get: (shareholderId: string) =>
      api.get<Shareholder>(`/shareholders/${shareholderId}`),

    getHoldings: (shareholderId: string) =>
      api.get<ShareHolding[]>(`/shareholders/${shareholderId}/holdings`),

    create: (companyId: string, data: CreateShareholderData) =>
      api.post<Shareholder>(`/companies/${companyId}/shareholders`, data),

    update: (shareholderId: string, data: UpdateShareholderData) =>
      api.patch<Shareholder>(`/shareholders/${shareholderId}`, data),

    delete: (shareholderId: string) =>
      api.delete<{ success: boolean }>(`/shareholders/${shareholderId}`),

    // Bulk import
    getImportTemplate: async (companyId: string): Promise<string> => {
      const headers: HeadersInit = {};
      const accessToken = api.getAccessToken();
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch(`${API_URL}/companies/${companyId}/shareholders/import/template`, {
        method: "GET",
        headers,
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Kunde inte hämta CSV-mall");
      }
      return response.text();
    },

    bulkImport: (companyId: string, csvData: string) =>
      api.post<BulkImportResult>(`/companies/${companyId}/shareholders/import`, { csvData }),
  },

  // Transactions
  transactions: {
    list: (companyId: string, params?: { limit?: number; offset?: number; type?: TransactionType }) => {
      const queryParams = new URLSearchParams();
      if (params?.limit) queryParams.set("limit", String(params.limit));
      if (params?.offset) queryParams.set("offset", String(params.offset));
      if (params?.type) queryParams.set("type", params.type);
      const query = queryParams.toString();
      return api.get<ShareTransaction[]>(`/companies/${companyId}/transactions${query ? `?${query}` : ""}`);
    },

    get: (transactionId: string) =>
      api.get<ShareTransaction>(`/transactions/${transactionId}`),

    registerFounding: (companyId: string, data: FoundingTransactionData) =>
      api.post<ShareTransaction>(`/companies/${companyId}/transactions/founding`, data),

    registerPurchase: (companyId: string, data: PurchaseTransactionData) =>
      api.post<ShareTransaction>(`/companies/${companyId}/transactions/purchase`, data),

    registerGift: (companyId: string, data: GiftTransactionData) =>
      api.post<ShareTransaction>(`/companies/${companyId}/transactions/gift`, data),

    registerInheritance: (companyId: string, data: InheritanceTransactionData) =>
      api.post<ShareTransaction>(`/companies/${companyId}/transactions/inheritance`, data),

    registerNewIssue: (companyId: string, data: NewIssueTransactionData) =>
      api.post<ShareTransaction>(`/companies/${companyId}/transactions/new-issue`, data),

    registerBonusIssue: (companyId: string, data: BonusIssueTransactionData) =>
      api.post<ShareTransaction>(`/companies/${companyId}/transactions/bonus-issue`, data),

    registerSplit: (companyId: string, data: SplitTransactionData) =>
      api.post<ShareTransaction>(`/companies/${companyId}/transactions/split`, data),

    registerReverseSplit: (companyId: string, data: ReverseSplitTransactionData) =>
      api.post<ShareTransaction>(`/companies/${companyId}/transactions/reverse-split`, data),

    registerReduction: (companyId: string, data: ReductionTransactionData) =>
      api.post<ShareTransaction>(`/companies/${companyId}/transactions/reduction`, data),

    registerConversion: (companyId: string, data: ConversionTransactionData) =>
      api.post<ShareTransaction>(`/companies/${companyId}/transactions/conversion`, data),

    registerRedemption: (companyId: string, data: RedemptionTransactionData) =>
      api.post<ShareTransaction>(`/companies/${companyId}/transactions/redemption`, data),

    registerPledge: (companyId: string, data: PledgeTransactionData) =>
      api.post<{ success: boolean; message: string }>(`/companies/${companyId}/transactions/pledge`, data),

    registerUnpledge: (companyId: string, data: UnpledgeTransactionData) =>
      api.post<{ success: boolean; message: string }>(`/companies/${companyId}/transactions/unpledge`, data),

    registerCorrection: (companyId: string, data: CorrectionTransactionData) =>
      api.post<ShareTransaction>(`/companies/${companyId}/transactions/correction`, data),

    verify: (transactionId: string) =>
      api.post<ShareTransaction>(`/transactions/${transactionId}/verify`),
  },

  // Events (Audit Trail)
  events: {
    list: (
      companyId: string,
      params?: { limit?: number; offset?: number; eventType?: string; entityType?: string; entityId?: string }
    ) => {
      const queryParams = new URLSearchParams();
      if (params?.limit) queryParams.set("limit", String(params.limit));
      if (params?.offset) queryParams.set("offset", String(params.offset));
      if (params?.eventType) queryParams.set("eventType", params.eventType);
      if (params?.entityType) queryParams.set("entityType", params.entityType);
      if (params?.entityId) queryParams.set("entityId", params.entityId);
      const query = queryParams.toString();
      return api.get<ShareBookEvent[]>(`/companies/${companyId}/share-book/events${query ? `?${query}` : ""}`);
    },

    getAsOfDate: (companyId: string, date: string) =>
      api.get<ShareBookEvent[]>(`/companies/${companyId}/share-book/events/as-of/${date}`),

    verifyIntegrity: (eventId: string) =>
      api.get<{ isValid: boolean }>(`/events/${eventId}/verify`),
  },
};

// ═══════════════════════════════════════════════════════════════════
// Integration Types & API
// ═══════════════════════════════════════════════════════════════════

export type IntegrationProvider =
  | "fortnox"
  | "visma"
  | "bjorn_lunden"
  | "pe_accounting"
  | "bokio"
  | "speedledger";

export type IntegrationStatus = "pending" | "active" | "expired" | "error" | "disconnected";

export interface IntegrationProviderInfo {
  provider: IntegrationProvider;
  name: string;
  displayName: string;
  description: string;
  logoUrl: string;
  website: string;
  docsUrl: string;
  available: boolean;
  sandboxAvailable: boolean;
}

export interface Integration {
  id: string;
  userId: string;
  companyId: string;
  provider: IntegrationProvider;
  externalCompanyId: string | null;
  externalCompanyName: string | null;
  scopes: string[] | null;
  status: IntegrationStatus;
  tokenExpiresAt: string | null;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastError: string | null;
  lastErrorAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IntegrationSyncData {
  type: "sie" | "accounts" | "structured";
  recordCount: number;
  data: unknown;
  calculations?: {
    keyFigures: ParsedSieData["keyFigures"];
    triggeredNotes: string[];
  };
  financialYear?: FortnoxFinancialYear;
}

export interface FortnoxFinancialYear {
  Id: number;
  FromDate: string; // YYYY-MM-DD
  ToDate: string; // YYYY-MM-DD
  AccountCharts: string;
  AccountingMethod: string;
}

export const integrationsApi = {
  /** List all available integration providers */
  getProviders: () =>
    api.get<{ providers: IntegrationProviderInfo[] }>("/integrations/providers"),

  /** List integrations for a company */
  getForCompany: (companyId: string) =>
    api.get<{ integrations: Integration[] }>(`/integrations/companies/${companyId}`),

  /** Start OAuth flow to connect a provider */
  connect: (companyId: string, provider: IntegrationProvider, returnUrl?: string) =>
    api.post<{ authUrl: string; state: string }>(
      `/integrations/companies/${companyId}/connect/${provider}`,
      returnUrl ? { returnUrl } : undefined
    ),

  /** Disconnect an integration */
  disconnect: (integrationId: string) =>
    api.post<{ message: string }>(`/integrations/${integrationId}/disconnect`),

  /** Sync data from an integration */
  sync: (integrationId: string, fiscalYear?: { start: string; end: string }) =>
    api.post<IntegrationSyncData>(`/integrations/${integrationId}/sync`, { fiscalYear }),

  /** Manually refresh the access token */
  refresh: (integrationId: string) =>
    api.post<{ message: string }>(`/integrations/${integrationId}/refresh`),

  /** Get available financial years from Fortnox */
  getFortnoxFinancialYears: (integrationId: string) =>
    api.get<{ financialYears: FortnoxFinancialYear[] }>(
      `/integrations/${integrationId}/fortnox/financial-years`
    ),

  // ═══════════════════════════════════════════════════════════════════
  // Guest Integration Methods (no authentication required)
  // ═══════════════════════════════════════════════════════════════════

  /** Start OAuth flow for guests (no account required)
   * @param guestOrgNumber - The org.nr entered by guest in wizard (stored and validated after OAuth)
   */
  guestConnect: (provider: IntegrationProvider, returnUrl?: string, guestOrgNumber?: string) =>
    api.post<{ authUrl: string; state: string }>("/integrations/guest-connect", { provider, returnUrl, guestOrgNumber }),

  /** Get fiscal years for a guest session */
  getGuestFiscalYears: (sessionKey: string) =>
    api.get<{ financialYears: FortnoxFinancialYear[] }>(
      `/integrations/guest-fiscal-years?session_key=${encodeURIComponent(sessionKey)}`
    ),

  /** Sync data for a guest session
   * @param guestOrgNumber - The org.nr entered by guest in wizard (validated against accounting system)
   */
  guestSync: (sessionKey: string, guestOrgNumber: string, fiscalYear?: { start: string; end: string }) =>
    api.post<IntegrationSyncData>("/integrations/guest-sync", { sessionKey, guestOrgNumber, fiscalYear }),

  /** Exchange OAuth code for tokens (called from frontend callback) */
  exchangeCode: (code: string, state: string) =>
    api.post<{
      isGuest: boolean;
      guestSessionKey?: string;
      provider?: string;
      externalCompanyName?: string;
      integration?: Integration;
      returnUrl?: string; // Return URL for redirecting back to wizard after OAuth
    }>("/integrations/exchange-code", { code, state }),
};

// ═══════════════════════════════════════════════════════════════════
// Protocol Types (Årsstämmoprotokoll - ABL 7 kap 48-53§)
// ═══════════════════════════════════════════════════════════════════

export type ProtocolType = "annual_meeting" | "board_meeting" | "constitutive_board" | "extraordinary_meeting";
export type ProtocolStatus = "draft" | "finalized";

// Extraordinary meeting reason types (ABL 7 kap 13-14§)
export type ExtraordinaryReasonType =
  | "capital_change"
  | "bylaw_amendment"
  | "board_change"
  | "minority_request"
  | "other";

export interface ProtocolAttendee {
  name: string;
  shares: number;
  votes: number;
  present: boolean;
  representedBy?: string;
}

export interface ProtocolDischargeDecision {
  name: string;
  role: string;
  granted: boolean;
  votesFor?: number;
  votesAgainst?: number;
}

export interface ProtocolProfitDisposition {
  followsBoardProposal: boolean;
  retainedEarnings: number;
  currentYearResult: number;
  dividend: number;
  carriedForward: number;
}

export interface ProtocolBoardElection {
  performed: boolean;
  members: Array<{
    name: string;
    role: string;
  }>;
}

export interface ProtocolAuditorElection {
  performed: boolean;
  auditor: string;
  firm?: string;
}

export interface ProtocolDecisions {
  financialStatementsApproved?: boolean;
  profitDisposition?: ProtocolProfitDisposition;
  discharge?: ProtocolDischargeDecision[];
  boardElection?: ProtocolBoardElection;
  auditorElection?: ProtocolAuditorElection;
}

// Constitutive Board Meeting Types
export interface ConstitutiveBoardDecisions {
  // Val av ordförande (§ 3)
  chairpersonElection: {
    name: string;
  };
  // Firmateckning (§ 4)
  signatureRights: {
    type: "board_together" | "single" | "two_together" | "custom";
    singlePerson?: string;
    twoPeople?: { person1: string; person2: string };
    customText?: string;
  };
  // Attestregler (§ 5) - valfritt
  attestRules?: {
    enabled: boolean;
    lowLimit?: number;
    lowAttester?: string;
    highAttester?: string;
  };
  // VD (§ 6) - valfritt
  ceoAppointment?: {
    appointed: boolean;
    name?: string;
  };
  // Bankkonton (§ 7) - valfritt
  bankAuthorization?: {
    enabled: boolean;
    authorizedPerson?: string;
    bank?: string;
  };
  // Nästa möte (§ 8) - valfritt
  nextMeeting?: {
    scheduled: boolean;
    date?: string;
  };
}

export interface ConstitutiveBoardPrefillData {
  company: {
    name: string;
    organizationNumber: string;
    seatMunicipality: string | null;
  };
  annualMeeting: {
    id: string;
    meetingDate: string;
    meetingPlace: string | null;
  };
  boardMembers: Array<{
    name: string;
    role: string;
  }>;
  suggestedChairperson?: string;
}

export interface AnnualMeetingWithBoardElection {
  id: string;
  meetingDate: string;
  fiscalYearStart: string;
  fiscalYearEnd: string;
  boardMembers: Array<{ name: string; role: string }>;
}

export interface Protocol {
  id: string;
  companyId: string;
  companyName: string;
  annualReportId: string | null;
  type: ProtocolType;
  meetingDate: string;
  meetingTime: string | null;
  meetingEndTime: string | null; // For board meetings
  meetingPlace: string | null;
  protocolNumber: string | null; // For board meetings (format: "1/2026")
  chairpersonName: string;
  secretaryName: string | null;
  adjusterName: string | null;
  attendees: ProtocolAttendee[];
  decisions: ProtocolDecisions;
  otherBusiness: string | null;
  status: ProtocolStatus;
  createdAt: string;
  updatedAt: string;
  finalizedAt: string | null;
  createdBy: string | null;
  finalizedBy: string | null;
}

// ═══════════════════════════════════════════════════════════════════
// Board Meeting Types (ABL 8 kap 24-25§)
// ═══════════════════════════════════════════════════════════════════

export type BoardMeetingRole = "chairman" | "member" | "deputy" | "ceo" | "other";

export interface BoardMeetingAttendee {
  name: string;
  role: BoardMeetingRole;
  roleTitle: string;
  present: boolean;
  absent?: boolean;
  absentReason?: string;
}

export interface BoardMeetingAdjunct {
  name: string;
  role: string;
  attendsItems?: string[];
}

export type BoardMeetingAgendaItemType =
  | "opening"
  | "adjuster"
  | "agenda"
  | "previous_protocol"
  | "other"
  | "next_meeting"
  | "closing"
  | "custom";

export interface BoardMeetingAgendaItem {
  itemNumber: number;
  title: string;
  description?: string;
  decision?: string;
  itemType?: BoardMeetingAgendaItemType;
}

export interface BoardMeetingDecisions {
  adjuncts?: BoardMeetingAdjunct[];
  previousProtocol?: {
    approved: boolean;
    protocolNumber?: string;
    date?: string;
  };
  agendaItems: BoardMeetingAgendaItem[];
  nextMeeting?: {
    scheduled: boolean;
    date?: string;
    time?: string;
    place?: string;
  };
}

export interface CreateBoardMeetingData {
  meetingDate: string;
  meetingTime?: string;
  meetingEndTime?: string;
  meetingPlace?: string;
  chairpersonName: string;
  secretaryName?: string;
  adjusterName?: string;
  attendees: BoardMeetingAttendee[];
  decisions: BoardMeetingDecisions;
  otherBusiness?: string;
}

export interface BoardMeetingPrefillData {
  company: {
    name: string;
    organizationNumber: string;
    seatMunicipality: string | null;
  };
  boardMembers: Array<{
    id: string;
    name: string;
    role: string;
    roleTitle: string;
  }>;
  nextProtocolNumber: string;
  previousProtocol?: {
    protocolNumber: string;
    meetingDate: string;
  };
  suggestedChairperson?: string;
  defaultAgendaItems: BoardMeetingAgendaItem[];
}

// ═══════════════════════════════════════════════════════════════════
// Extraordinary Meeting Types (ABL 7 kap 13-14§)
// ═══════════════════════════════════════════════════════════════════

export interface ExtraordinaryMeetingDecisions {
  reason: ExtraordinaryReasonType;
  reasonDescription?: string;
  capitalChange?: {
    type: "increase" | "decrease";
    amount: number;
    method?: string;
    description?: string;
  };
  bylawAmendment?: {
    sections: string[];
    description: string;
  };
  boardElection?: {
    performed: boolean;
    members: Array<{ name: string; role: string }>;
  };
  otherDecisions?: Array<{
    title: string;
    decision: string;
  }>;
}

export interface ExtraordinaryMeetingAttendee {
  name: string;
  shares: number;
  votes: number;
  present: boolean;
  representedBy?: string;
}

export interface CreateExtraordinaryMeetingData {
  meetingDate: string;
  meetingTime?: string;
  meetingPlace?: string;
  chairpersonName: string;
  secretaryName?: string;
  adjusterName?: string;
  attendees: ExtraordinaryMeetingAttendee[];
  decisions: ExtraordinaryMeetingDecisions;
  otherBusiness?: string;
}

export interface ExtraordinaryMeetingPrefillData {
  company: {
    name: string;
    organizationNumber: string;
    seatMunicipality: string | null;
  };
  shareholders: Array<{
    id: string;
    name: string;
    totalShares: number;
    totalVotes: number;
  }>;
  boardMembers: Array<{
    id: string;
    name: string;
    role: string;
  }>;
  reasonOptions: Array<{
    value: ExtraordinaryReasonType;
    label: string;
  }>;
}

export interface ProtocolSummary {
  id: string;
  companyId: string;
  companyName: string;
  annualReportId: string | null;
  type: ProtocolType;
  meetingDate: string;
  status: ProtocolStatus;
  chairpersonName: string;
  createdAt: string;
  finalizedAt: string | null;
}

export interface CreateProtocolData {
  annualReportId?: string;
  linkedAnnualMeetingId?: string; // For constitutive_board protocols
  agendaId?: string; // Link to sent agenda for tracking
  type?: ProtocolType;
  meetingDate: string;
  meetingTime?: string;
  meetingPlace?: string;
  chairpersonName: string;
  secretaryName?: string;
  adjusterName?: string;
  attendees?: ProtocolAttendee[];
  decisions?: ProtocolDecisions | ConstitutiveBoardDecisions;
  otherBusiness?: string;
}

export interface UpdateProtocolData {
  meetingDate?: string;
  meetingTime?: string | null;
  meetingPlace?: string | null;
  chairpersonName?: string;
  secretaryName?: string | null;
  adjusterName?: string | null;
  attendees?: ProtocolAttendee[];
  decisions?: ProtocolDecisions;
  otherBusiness?: string | null;
}

export interface ProtocolPrefillData {
  company: {
    name: string;
    organizationNumber: string;
    seatMunicipality: string | null;
    address?: string;
    city?: string;
  };
  boardMembers: Array<{ id: string; name: string; role: string }>;
  shareholders: Array<{
    id: string;
    name: string;
    totalShares: number;
    totalVotes: number;
  }>;
  annualReport?: {
    id: string;
    fiscalYearStart: string;
    fiscalYearEnd: string;
    profitDisposition?: ProtocolProfitDisposition & { followsBoardProposal: boolean };
  };
  deadlines: {
    annualMeetingDeadline: string;
    filingDeadline: string;
  };
  availableAnnualReports: Array<{
    id: string;
    fiscalYearStart: string;
    fiscalYearEnd: string;
    status: string;
    hasProtocol: boolean;
  }>;
}

// Protocols API
export const protocolsApi = {
  list: (companyId: string) =>
    api.get<{ protocols: ProtocolSummary[] }>(`/protocols/${companyId}`),

  get: (companyId: string, protocolId: string) =>
    api.get<{ protocol: Protocol }>(`/protocols/${companyId}/${protocolId}`),

  getPrefill: (companyId: string, annualReportId?: string) =>
    api.get<ProtocolPrefillData>(
      `/protocols/${companyId}/prefill${annualReportId ? `?annualReportId=${annualReportId}` : ""}`
    ),

  create: (companyId: string, data: CreateProtocolData) =>
    api.post<{ protocol: Protocol }>(`/protocols/${companyId}`, data),

  update: (companyId: string, protocolId: string, data: UpdateProtocolData) =>
    api.patch<{ protocol: Protocol }>(`/protocols/${companyId}/${protocolId}`, data),

  finalize: (companyId: string, protocolId: string) =>
    api.post<{ protocol: Protocol }>(`/protocols/${companyId}/${protocolId}/finalize`),

  delete: (companyId: string, protocolId: string) =>
    api.delete<{ success: boolean }>(`/protocols/${companyId}/${protocolId}`),

  downloadPdf: async (
    companyId: string,
    protocolId: string
  ): Promise<{ success: boolean; blob?: Blob; filename?: string; error?: string }> => {
    try {
      const token = api.getAccessToken();
      const response = await fetch(`${API_URL}/protocols/${companyId}/${protocolId}/pdf`, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error?.message || "Kunde inte generera PDF" };
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const filename =
        contentDisposition?.match(/filename="(.+)"/)?.[1] || "arsstammoprotokoll.pdf";

      return { success: true, blob, filename };
    } catch {
      return { success: false, error: "Ett fel uppstod vid nedladdning" };
    }
  },

  // Constitutive Board Meeting APIs
  getAnnualMeetingsWithBoardElection: (companyId: string) =>
    api.get<{ annualMeetings: AnnualMeetingWithBoardElection[] }>(
      `/protocols/${companyId}/annual-meetings-with-election`
    ),

  getConstitutivePrefill: (companyId: string, annualMeetingId: string) =>
    api.get<ConstitutiveBoardPrefillData>(
      `/protocols/${companyId}/constitutive-prefill?annualMeetingId=${annualMeetingId}`
    ),

  // Board Meeting APIs (ABL 8 kap 24-25§)
  getBoardMeetingPrefill: (companyId: string) =>
    api.get<BoardMeetingPrefillData>(`/protocols/${companyId}/board-meeting-prefill`),

  createBoardMeeting: (companyId: string, data: CreateBoardMeetingData) =>
    api.post<{ protocol: Protocol }>(`/protocols/${companyId}/board-meeting`, data),

  // Extraordinary Meeting APIs (ABL 7 kap 13-14§)
  getExtraordinaryMeetingPrefill: (companyId: string) =>
    api.get<ExtraordinaryMeetingPrefillData>(`/protocols/${companyId}/extraordinary-meeting-prefill`),

  createExtraordinaryMeeting: (companyId: string, data: CreateExtraordinaryMeetingData) =>
    api.post<{ protocol: Protocol }>(`/protocols/${companyId}/extraordinary-meeting`, data),
};

// ═══════════════════════════════════════════════════════════════════
// Agendas Types & API
// ═══════════════════════════════════════════════════════════════════

export type AgendaStatus = "draft" | "sent" | "used";
export type AgendaMeetingType = "annual_meeting" | "board_meeting" | "constitutive_board";
export type AgendaItemType = "standard" | "custom";
export type AgendaRecipientSource = "shareholder" | "board_member" | "manual";

export interface Agenda {
  id: string;
  companyId: string;
  annualReportId: string | null;
  meetingType: AgendaMeetingType;
  meetingDate: string;
  meetingTime: string | null;
  meetingPlace: string | null;
  status: AgendaStatus;
  createdAt: string;
  updatedAt: string;
  sentAt: string | null;
  createdBy: string | null;
}

export interface AgendaWithDetails extends Agenda {
  companyName: string;
  organizationNumber: string;
  items: AgendaItem[];
  recipients: AgendaRecipient[];
  fiscalYearStart?: string;
  fiscalYearEnd?: string;
}

export interface AgendaItem {
  id: string;
  agendaId: string;
  itemNumber: number;
  title: string;
  description: string | null;
  itemType: AgendaItemType;
  standardItemKey: string | null;
  isRequired: boolean;
  sortOrder: number;
  createdAt: string;
}

export interface AgendaRecipient {
  id: string;
  agendaId: string;
  name: string;
  email: string | null;
  personalIdNormalized: string | null;
  source: AgendaRecipientSource;
  sourceId: string | null;
  emailSentAt: string | null;
  emailOpenedAt: string | null;
  createdAt: string;
}

export interface CreateAgendaData {
  annualReportId?: string;
  meetingType?: AgendaMeetingType;
  meetingDate: string;
  meetingTime?: string;
  meetingPlace?: string;
  includeOptionalItems?: string[];
  customItems?: Array<{ title: string; description?: string }>;
}

export interface UpdateAgendaData {
  meetingDate?: string;
  meetingTime?: string | null;
  meetingPlace?: string | null;
}

export interface AddAgendaItemData {
  title: string;
  description?: string;
  insertAfterKey?: string;
}

export interface AddRecipientData {
  name: string;
  email?: string;
  personalIdNormalized?: string;
  source?: AgendaRecipientSource;
  sourceId?: string;
}

export interface EmailLookupResult {
  name: string;
  email: string | null;
  personalIdNormalized: string;
  source: AgendaRecipientSource;
  sourceId: string;
}

export interface SendAgendaEmailsResult {
  recipientId: string;
  success: boolean;
  error?: string;
}

// Agendas API
export const agendasApi = {
  // Agenda CRUD
  list: (companyId: string, options?: { meetingType?: AgendaMeetingType; status?: AgendaStatus }) => {
    const params = new URLSearchParams();
    if (options?.meetingType) params.set("meetingType", options.meetingType);
    if (options?.status) params.set("status", options.status);
    const query = params.toString();
    return api.get<{ agendas: Agenda[] }>(
      `/companies/${companyId}/agendas${query ? `?${query}` : ""}`
    );
  },

  get: (companyId: string, agendaId: string) =>
    api.get<{ agenda: AgendaWithDetails }>(`/companies/${companyId}/agendas/${agendaId}`),

  create: (companyId: string, data: CreateAgendaData) =>
    api.post<{ agenda: Agenda }>(`/companies/${companyId}/agendas`, data),

  update: (companyId: string, agendaId: string, data: UpdateAgendaData) =>
    api.patch<{ agenda: Agenda }>(`/companies/${companyId}/agendas/${agendaId}`, data),

  delete: (companyId: string, agendaId: string) =>
    api.delete<{ success: boolean }>(`/companies/${companyId}/agendas/${agendaId}`),

  // Agenda Items
  addItem: (companyId: string, agendaId: string, data: AddAgendaItemData) =>
    api.post<{ item: AgendaItem }>(`/companies/${companyId}/agendas/${agendaId}/items`, data),

  updateItem: (companyId: string, agendaId: string, itemId: string, data: { title?: string; description?: string | null }) =>
    api.patch<{ item: AgendaItem }>(`/companies/${companyId}/agendas/${agendaId}/items/${itemId}`, data),

  deleteItem: (companyId: string, agendaId: string, itemId: string) =>
    api.delete<{ success: boolean }>(`/companies/${companyId}/agendas/${agendaId}/items/${itemId}`),

  reorderItems: (companyId: string, agendaId: string, itemIds: string[]) =>
    api.patch<{ success: boolean }>(`/companies/${companyId}/agendas/${agendaId}/items/reorder`, { itemIds }),

  // Recipients
  listRecipients: (companyId: string, agendaId: string) =>
    api.get<{ recipients: AgendaRecipient[] }>(`/companies/${companyId}/agendas/${agendaId}/recipients`),

  autoPopulateRecipients: (companyId: string, agendaId: string) =>
    api.post<{ recipients: AgendaRecipient[] }>(`/companies/${companyId}/agendas/${agendaId}/recipients/auto`),

  addRecipient: (companyId: string, agendaId: string, data: AddRecipientData) =>
    api.post<{ recipient: AgendaRecipient }>(`/companies/${companyId}/agendas/${agendaId}/recipients`, data),

  updateRecipientEmail: (companyId: string, agendaId: string, recipientId: string, email: string) =>
    api.patch<{ recipient: AgendaRecipient }>(`/companies/${companyId}/agendas/${agendaId}/recipients/${recipientId}`, { email }),

  removeRecipient: (companyId: string, agendaId: string, recipientId: string) =>
    api.delete<{ success: boolean }>(`/companies/${companyId}/agendas/${agendaId}/recipients/${recipientId}`),

  // Email
  sendEmails: (companyId: string, agendaId: string, recipientIds: string[]) =>
    api.post<{ results: SendAgendaEmailsResult[] }>(`/companies/${companyId}/agendas/${agendaId}/send-emails`, { recipientIds }),

  // Email Lookup
  lookupEmails: (companyId: string, personalIds?: string[]) => {
    const query = personalIds?.length ? `?personalIds=${personalIds.join(",")}` : "";
    return api.get<{ results: EmailLookupResult[] }>(`/companies/${companyId}/email-lookup${query}`);
  },
};
